import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useSearchParams, useNavigate } from "react-router-dom";
import {
  Users, Buildings, CurrencyInr, ChartLineUp, ShieldStar, Trash, Sparkle,
  Barbell, UsersFour, MagnifyingGlass, Crown, Trophy, Brain, ChatCircleDots,
  UserPlus, CalendarBlank, Lightning, FileText, DownloadSimple, ToggleRight, Plus,
  Heartbeat, EyeSlash, Eye, Flag, Ticket, Receipt, Megaphone, TestTube, UserSwitch,
  User, Warning, Envelope, Copy, PaperPlaneTilt, Pulse, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";

const TABS = [
  { id: "overview", label: "Overview", icon: ChartLineUp },
  { id: "user-detail", label: "User Detail", icon: User, hidden: true },
  { id: "revenue", label: "Revenue", icon: CurrencyInr },
  { id: "cohorts", label: "Cohorts", icon: Users },
  { id: "users", label: "Users", icon: Users },
  { id: "academies", label: "Academies", icon: Buildings },
  { id: "activity", label: "Academy Activity", icon: Pulse },
  { id: "leads", label: "Leads", icon: Envelope },
  { id: "payments", label: "Payments", icon: CurrencyInr },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "refunds", label: "Refunds", icon: Receipt },
  { id: "broadcast", label: "Broadcast", icon: Megaphone },
  { id: "seeder", label: "Seeder", icon: TestTube },
  { id: "impersonation", label: "Impersonation Log", icon: UserSwitch },
  { id: "reports", label: "Reports", icon: FileText },
  { id: "announcements", label: "Announcements", icon: Lightning },
  { id: "audit", label: "Audit Log", icon: FileText },
  { id: "consent", label: "Consent Log", icon: ShieldStar },
  { id: "flags", label: "Feature Flags", icon: ToggleRight },
  { id: "roles", label: "Admin Roles", icon: ShieldStar },
  { id: "killswitch", label: "Killswitch", icon: Warning },
  { id: "health", label: "Health", icon: Heartbeat },
  { id: "moderation", label: "Moderation", icon: Flag },
];

// Download a URL as a file after fetching with the auth token. Used by all
// Super Admin CSV export buttons. Falls back to a toast on failure.
async function downloadCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  } catch {
    toast.error("Export failed");
  }
}

async function downloadFile(path, filename, mime) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : "overview";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: false });
  const [newLeads, setNewLeads] = useState(0);

  const isSuperAdmin = user?.role === "super_admin" || user?.primary_role === "super_admin";

  useEffect(() => {
    if (authLoading || !isSuperAdmin) return;
    api.get("/admin/demo-requests?status=new")
      .then(({ data }) => setNewLeads(data?.counts?.new || 0))
      .catch(() => {});
  }, [authLoading, isSuperAdmin, tab]);

  if (!authLoading && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="label-tiny text-heat tracking-widest mb-2 inline-flex items-center gap-1">
          <ShieldStar size={12} weight="fill" /> // PLATFORM · SUPER ADMIN
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl">Command Deck</h1>
        <p className="text-text-secondary mt-2 text-sm">
          Platform-wide visibility. Handle users, academies, and revenue from here.
        </p>
      </div>

      <GlobalSearchBar />

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
        {TABS.filter((t) => !t.hidden).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm label-tiny tracking-wider transition border-b-2 -mb-px ${
              tab === t.id
                ? "border-heat text-heat"
                : "border-transparent text-text-secondary hover:text-heat"
            }`}
          >
            <t.icon size={14} weight="duotone" /> {t.label.toUpperCase()}
            {t.id === "leads" && newLeads > 0 && (
              <span
                data-testid="leads-tab-badge"
                className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center bg-heat text-[#0A0A0F] font-heading text-[10px] tabular-nums leading-none rounded-full"
              >
                {newLeads}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab newLeads={newLeads} onGoToLeads={() => setTab("leads")} />}
      {tab === "user-detail" && <UserDetailTab userId={searchParams.get("user_id")} onBack={() => setTab("users")} />}
      {tab === "revenue" && <RevenueTab />}
      {tab === "cohorts" && <CohortsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "academies" && <AcademiesTab />}
      {tab === "activity" && <AcademyActivityTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "coupons" && <CouponsTab />}
      {tab === "refunds" && <RefundsTab />}
      {tab === "broadcast" && <BroadcastTab />}
      {tab === "seeder" && <SeederTab />}
      {tab === "impersonation" && <ImpersonationLogTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "announcements" && <AnnouncementsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "consent" && <ConsentAuditTab />}
      {tab === "flags" && <FlagsTab />}
      {tab === "roles" && <RolesTab />}
      {tab === "killswitch" && <KillswitchTab />}
      {tab === "health" && <HealthTab />}
      {tab === "moderation" && <ModerationTab />}
    </div>
  );
}

const StatCard = ({ icon, label, value, sublabel, accent }) => (
  <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={accent === "heat" ? "text-heat mb-2" : "text-brand-accent mb-2"}>{icon}</div>
    <div className="text-3xl font-heading tabular-nums">{value}</div>
    <div className="label-tiny text-[10px] text-text-secondary mt-1">{label}</div>
    {sublabel && <div className="text-[11px] text-text-secondary mt-1">{sublabel}</div>}
  </div>
);

const OverviewTab = ({ newLeads = 0, onGoToLeads }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="label-tiny text-text-secondary" data-testid="admin-overview-loading">Loading...</div>;

  const maxSignups = Math.max(1, ...data.growth.signup_series_7d.map(d => d.count));
  const totalRoleUsers = Math.max(1, Object.values(data.roles).reduce((a, b) => a + b, 0));

  const evtStyle = {
    signup:  { icon: <UserPlus size={14} weight="duotone" />, color: "text-brand-accent" },
    payment: { icon: <CurrencyInr size={14} weight="duotone" />, color: "text-heat" },
    academy: { icon: <Buildings size={14} weight="duotone" />, color: "text-brand-accent" },
    team:    { icon: <UsersFour size={14} weight="duotone" />, color: "text-brand-accent" },
    workout: { icon: <Barbell size={14} weight="duotone" />, color: "text-text-secondary" },
  };

  const roleLabels = {
    super_admin: "SUPER ADMINS",
    academy_admin: "ACADEMY ADMINS",
    strength_coach: "STRENGTH COACHES",
    fitness_trainer: "FITNESS TRAINERS",
    sports_coach: "SPORTS COACHES",
    athlete: "ATHLETES",
    general_client: "GENERAL CLIENTS",
    parent: "PARENTS",
    // Legacy fallbacks (in case backend still emits them briefly)
    individual: "INDIVIDUALS",
    coach: "COACHES",
  };

  const roleTint = {
    super_admin: "bg-red-500",
    academy_admin: "bg-yellow-400",
    strength_coach: "bg-heat",
    fitness_trainer: "bg-lime-400",
    sports_coach: "bg-cyan-400",
    athlete: "bg-brand-accent",
    general_client: "bg-white/60",
    parent: "bg-amber-400",
  };

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {newLeads > 0 && (
        <button
          onClick={onGoToLeads}
          data-testid="overview-new-leads-banner"
          className="w-full flex items-center justify-between gap-4 border border-heat/40 bg-heat/10 hover:bg-heat/20 transition-colors p-4 text-left"
        >
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 flex-shrink-0 bg-heat text-[#0A0A0F] flex items-center justify-center rounded-full">
              <Envelope size={18} weight="fill" />
            </span>
            <div>
              <div className="font-heading text-lg leading-none">
                {newLeads} NEW {newLeads === 1 ? "LEAD" : "LEADS"}
              </div>
              <div className="text-[11px] text-text-secondary mt-1">Demo / contact requests waiting for a response</div>
            </div>
          </div>
          <span className="label-tiny text-heat inline-flex items-center gap-1">REVIEW →</span>
        </button>
      )}
      <OpsInbox />
      {/* Scale */}
      <div>
        <div className="label-tiny text-text-secondary mb-3">SCALE</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard icon={<Users size={20} weight="duotone" />} label="TOTAL USERS" value={data.totals.users} sublabel={`+${data.growth.signups_24h} in 24h`} />
          <StatCard icon={<Lightning size={20} weight="duotone" />} label="ACTIVE / 7D" value={data.growth.active_users_7d} accent="heat" />
          <StatCard icon={<Sparkle size={20} weight="duotone" />} label="COACHES" value={data.totals.coaches} />
          <StatCard icon={<Buildings size={20} weight="duotone" />} label="ACADEMIES" value={data.totals.academies} />
          <StatCard icon={<UsersFour size={20} weight="duotone" />} label="TEAMS" value={data.totals.teams} />
          <StatCard icon={<Barbell size={20} weight="duotone" />} label="WORKOUTS" value={data.totals.workouts_logged.toLocaleString()} />
          <StatCard icon={<Brain size={20} weight="duotone" />} label="AI PROGRAMS" value={data.totals.ai_programs_generated.toLocaleString()} />
          <StatCard icon={<ChatCircleDots size={20} weight="duotone" />} label="MESSAGES" value={data.totals.messages_sent.toLocaleString()} />
        </div>
      </div>

      {/* Monetization */}
      <div>
        <div className="label-tiny text-text-secondary mb-3">MONETIZATION</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard accent="heat" icon={<CurrencyInr size={20} weight="duotone" />} label="REVENUE ALL TIME" value={`₹${data.monetization.revenue_inr_total.toLocaleString()}`} sublabel={`${data.monetization.paid_transactions} txns`} />
          <StatCard accent="heat" icon={<CurrencyInr size={20} weight="duotone" />} label="REVENUE / 30D" value={`₹${data.monetization.revenue_inr_30d.toLocaleString()}`} />
          <StatCard accent="heat" icon={<CurrencyInr size={20} weight="duotone" />} label="REVENUE / 7D" value={`₹${data.monetization.revenue_inr_7d.toLocaleString()}`} />
          <StatCard icon={<Crown size={20} weight="duotone" />} label="PRO ACTIVE" value={data.monetization.pro_active} />
          <StatCard icon={<Trophy size={20} weight="duotone" />} label="COACH PRO ACTIVE" value={data.monetization.coach_pro_active} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Signup chart */}
        <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="admin-signup-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tiny text-text-secondary">SIGNUPS / LAST 7 DAYS</div>
            <div className="text-2xl font-heading text-brand-accent tabular-nums">{data.growth.signups_7d}</div>
          </div>
          <div className="flex items-end gap-2 h-32">
            {data.growth.signup_series_7d.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-brand-accent/70 hover:bg-brand-accent transition" style={{ height: `${(d.count / maxSignups) * 100}%`, minHeight: d.count > 0 ? "4px" : "0" }} title={`${d.count} signups`} />
                <div className="text-[10px] label-tiny text-text-secondary">{d.day}</div>
                <div className="text-[10px] font-bold tabular-nums">{d.count}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Role distribution */}
        <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="admin-role-dist">
          <div className="label-tiny text-text-secondary mb-4">ROLE DISTRIBUTION</div>
          <div className="space-y-2">
            {Object.entries(data.roles).map(([role, count]) => (
              <div key={role} className="flex items-center gap-3" data-testid={`admin-role-${role}`}>
                <div className="w-36 label-tiny text-[10px]">{roleLabels[role] || role.toUpperCase().replace(/_/g, ' ')}</div>
                <div className="flex-1 h-5 bg-white/5 relative">
                  <div className={`h-full ${roleTint[role] || 'bg-brand-accent/60'}`} style={{ width: `${(count / totalRoleUsers) * 100}%` }} />
                </div>
                <div className="w-10 text-right text-sm font-heading tabular-nums">{count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent activity */}
        <div className="lg:col-span-2 border border-white/10 bg-white/[0.02] p-5" data-testid="admin-activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tiny text-text-secondary">RECENT ACTIVITY</div>
            <div className="text-[10px] label-tiny text-text-secondary/70">LIVE</div>
          </div>
          <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
            {data.recent_activity.length === 0 && (
              <div className="text-xs text-text-secondary py-4">No recent activity.</div>
            )}
            {data.recent_activity.map((e, i) => {
              const s = evtStyle[e.type] || evtStyle.workout;
              return (
                <div key={i} className="py-2.5 flex items-start gap-3" data-testid={`admin-activity-${i}`}>
                  <div className={s.color}>{s.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs"><span className="font-medium">{e.who}</span> <span className="text-text-secondary">{e.detail}</span></div>
                    <div className="text-[10px] label-tiny text-text-secondary">
                      {e.type.toUpperCase()}{e.role ? ` · ${e.role.toUpperCase()}` : ""} · {e.at ? new Date(e.at).toLocaleString() : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top coaches */}
        <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="admin-top-coaches">
          <div className="label-tiny text-text-secondary mb-4">TOP COACHES BY ROSTER</div>
          {data.top_coaches.length === 0 ? (
            <div className="text-xs text-text-secondary">No coach relationships yet.</div>
          ) : (
            <div className="space-y-3">
              {data.top_coaches.map((c, i) => (
                <div key={c.user_id} className="flex items-center gap-3" data-testid={`admin-top-coach-${i}`}>
                  <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${i === 0 ? "bg-heat text-white" : "bg-white/5"}`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[10px] label-tiny text-text-secondary truncate">{c.email}</div>
                  </div>
                  <div className="text-sm font-heading text-brand-accent tabular-nums">{c.clients}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="text-[10px] label-tiny text-text-secondary/70 text-right" data-testid="admin-generated-at">
        Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : ""}
      </div>
    </div>
  );
};

const ROLE_CHIP = {
  super_admin:     { label: "SUPER ADMIN",     cls: "bg-red-500/15 text-red-400" },
  academy_admin:   { label: "ACADEMY ADMIN",   cls: "bg-yellow-400/15 text-yellow-300" },
  strength_coach:  { label: "STRENGTH COACH",  cls: "bg-heat/15 text-heat" },
  fitness_trainer: { label: "FITNESS TRAINER", cls: "bg-lime-400/15 text-lime-300" },
  sports_coach:    { label: "SPORTS COACH",    cls: "bg-cyan-400/15 text-cyan-300" },
  athlete:         { label: "ATHLETE",         cls: "bg-brand-accent/15 text-brand-accent" },
  general_client:  { label: "GENERAL CLIENT",  cls: "bg-white/10 text-white/80" },
  parent:          { label: "PARENT",          cls: "bg-amber-400/15 text-amber-300" },
};

const ROLE_FILTER_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "super_admin", label: "Super Admin" },
  { value: "academy_admin", label: "Academy Admin" },
  { value: "strength_coach", label: "Strength Coach" },
  { value: "fitness_trainer", label: "Fitness Trainer" },
  { value: "sports_coach", label: "Sports Coach" },
  { value: "athlete", label: "Athlete" },
  { value: "general_client", label: "General Client" },
  { value: "parent", label: "Parent" },
];

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [roleEditFor, setRoleEditFor] = useState(null); // user object being edited
  const [supportFor, setSupportFor] = useState(null); // user object for support dialog

  const load = useCallback(() => {
    setBusy(true);
    const params = new URLSearchParams();
    params.set("limit", "100");
    if (search) params.set("search", search);
    if (roleFilter) params.set("primary_role", roleFilter);
    api.get(`/admin/users?${params.toString()}`)
      .then(({ data }) => { setUsers(data.items); setTotal(data.total); })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setBusy(false));
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const grantPro = async (uid, days, coach = false) => {
    const body = coach ? { grant_coach_pro_days: days } : { grant_pro_days: days };
    try {
      await api.patch(`/admin/users/${uid}`, body);
      toast.success(`Granted ${days}d`);
      load();
    } catch { toast.error("Failed"); }
  };
  const toggleCoach = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_coach: !u.is_coach });
      toast.success(u.is_coach ? "Coach disabled" : "Coach enabled");
      load();
    } catch { toast.error("Failed"); }
  };
  const toggleAdmin = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role: u.role === "super_admin" ? "user" : "super_admin" });
      toast.success("Platform role updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changePrimaryRole = async (uid, newRole) => {
    try {
      await api.patch(`/admin/users/${uid}`, { primary_role: newRole });
      toast.success("Primary role updated");
      setRoleEditFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <Input
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-users-search"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="admin-users-role-filter"
          className="bg-transparent border border-white/10 text-sm px-3 py-2 text-white focus:outline-none focus:border-heat"
        >
          {ROLE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} className="bg-surface-DEFAULT">{o.label}</option>
          ))}
        </select>
        <div className="text-xs text-text-secondary ml-auto">{total} total</div>
        <button
          onClick={() => downloadCsv("/admin/users/export.csv", "edge-users.csv")}
          className="inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors"
          data-testid="admin-users-export-csv"
        >
          <DownloadSimple size={12} weight="bold" /> EXPORT CSV
        </button>
      </div>
      {busy && <div className="label-tiny text-text-secondary" data-testid="admin-users-loading">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-users-list">
        {users.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm" data-testid="admin-users-empty">
            No users match this filter.
          </div>
        )}
        {users.map((u) => {
          const chip = ROLE_CHIP[u.primary_role] || { label: (u.primary_role || "USER").toUpperCase().replace(/_/g, ' '), cls: "bg-white/10 text-white/70" };
          return (
          <div key={u.user_id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-user-row-${u.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate flex items-center gap-2 flex-wrap">
                {u.name}
                <span className={`label-tiny text-[9px] px-2 py-0.5 ${chip.cls}`} data-testid={`admin-user-role-chip-${u.user_id}`}>{chip.label}</span>
                {u.role === "super_admin" && <span className="label-tiny text-[9px] bg-red-500/15 text-red-400 px-2 py-0.5">ADMIN</span>}
                {u.is_pro && <span className="label-tiny text-[9px] bg-green-500/15 text-green-600 px-2 py-0.5">PRO</span>}
                {u.is_coach_pro && <span className="label-tiny text-[9px] bg-brand-accent text-surface-base px-2 py-0.5">COACH PRO</span>}
              </div>
              <div className="text-[11px] text-text-secondary truncate">{u.email}</div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setRoleEditFor(u)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-white/10 hover:border-heat hover:text-heat transition"
                data-testid={`admin-change-role-${u.user_id}`}
              >CHANGE ROLE</button>
              <button
                onClick={() => grantPro(u.user_id, 30)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-white/10 hover:border-brand-accent hover:text-brand-accent transition"
                data-testid={`admin-grant-pro-${u.user_id}`}
              >+30D PRO</button>
              <button
                onClick={() => grantPro(u.user_id, 30, true)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-white/10 hover:border-brand-accent hover:text-brand-accent transition"
                data-testid={`admin-grant-coach-pro-${u.user_id}`}
              >+30D COACH PRO</button>
              <button
                onClick={() => toggleCoach(u)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-white/10 hover:border-brand-accent hover:text-brand-accent transition"
                data-testid={`admin-toggle-coach-${u.user_id}`}
              >{u.is_coach ? "REVOKE COACH" : "MAKE COACH"}</button>
              <button
                onClick={() => toggleAdmin(u)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-white/10 hover:border-heat hover:text-heat transition"
                data-testid={`admin-toggle-admin-${u.user_id}`}
              >{u.role === "super_admin" ? "DEMOTE ADMIN" : "PROMOTE ADMIN"}</button>
              <button
                onClick={() => setSupportFor(u)}
                className="text-[10px] label-tiny tracking-wider px-2 py-1.5 border border-brand-heat/40 text-brand-heat hover:!bg-brand-heat hover:!text-white transition"
                data-testid={`admin-support-${u.user_id}`}
              >SUPPORT</button>
              <button
                onClick={() => deleteUser(u)}
                className="p-1.5 text-text-secondary hover:text-red-500 transition"
                data-testid={`admin-delete-user-${u.user_id}`}
                aria-label="Delete user"
              >
                <Trash size={14} />
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {roleEditFor && (
        <ChangeRoleDialog
          user={roleEditFor}
          onClose={() => setRoleEditFor(null)}
          onSave={(newRole) => changePrimaryRole(roleEditFor.user_id, newRole)}
        />
      )}
      {supportFor && (
        <SupportDialog
          user={supportFor}
          onClose={() => setSupportFor(null)}
        />
      )}
    </div>
  );
};

const SupportDialog = ({ user, onClose }) => {
  const [busy, setBusy] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [msgTitle, setMsgTitle] = useState("");
  const [msgBody, setMsgBody] = useState("");

  const impersonate = async () => {
    if (!window.confirm(`Impersonate ${user.email}? You'll be logged in as them.`)) return;
    setBusy("impersonate");
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/impersonate`);
      if (!data?.token) throw new Error("no token");
      localStorage.setItem("token", data.token);
      toast.success(`Now logged in as ${user.email} — refreshing…`);
      setTimeout(() => window.location.assign("/"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not impersonate");
    } finally {
      setBusy("");
    }
  };

  const forceLogout = async () => {
    if (!window.confirm(`Force logout ${user.email}? They'll be signed out on every device.`)) return;
    setBusy("logout");
    try {
      await api.post(`/admin/users/${user.user_id}/force-logout`, { reason: "admin action" });
      toast.success("Logged out on all devices");
    } catch {
      toast.error("Could not force logout");
    } finally {
      setBusy("");
    }
  };

  const resetPw = async () => {
    if (!window.confirm(`Reset ${user.email}'s password? They'll be logged out and you'll get a temp password to share.`)) return;
    setBusy("reset");
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/reset-password`);
      setNewPassword(data?.new_password || "");
      toast.success("Password reset — copy the temp password below");
    } catch {
      toast.error("Could not reset password");
    } finally {
      setBusy("");
    }
  };

  const sendMessage = async () => {
    if (!msgTitle.trim()) { toast.error("Title required"); return; }
    setBusy("message");
    try {
      await api.post(`/admin/users/${user.user_id}/send-message`, { title: msgTitle, body: msgBody });
      toast.success("Message sent");
      setMsgTitle(""); setMsgBody("");
    } catch {
      toast.error("Could not send message");
    } finally {
      setBusy("");
    }
  };

  const toggleVerify = async () => {
    setBusy("verify");
    try {
      await api.patch(`/admin/users/${user.user_id}/verify`, { verified: !user.is_verified });
      toast.success(user.is_verified ? "Verification removed" : "Verified ✓");
      user.is_verified = !user.is_verified;
    } catch {
      toast.error("Could not toggle verify");
    } finally { setBusy(""); }
  };

  const toggleSuspend = async () => {
    const isSuspended = !!user.suspended_until;
    if (isSuspended) {
      if (!window.confirm(`Lift suspension for ${user.email}?`)) return;
      setBusy("suspend");
      try {
        await api.patch(`/admin/users/${user.user_id}/suspend`, { until: "" });
        toast.success("Suspension lifted");
        user.suspended_until = null;
      } catch { toast.error("Could not lift"); } finally { setBusy(""); }
      return;
    }
    const days = window.prompt(`Suspend ${user.email} for how many days?`, "7");
    if (!days) return;
    const n = parseInt(days, 10);
    if (isNaN(n) || n < 1) return toast.error("Enter a positive number of days");
    const reason = window.prompt("Reason (optional)") || "";
    const until = new Date(Date.now() + n * 86400000).toISOString();
    setBusy("suspend");
    try {
      await api.patch(`/admin/users/${user.user_id}/suspend`, { until, reason });
      toast.success(`Suspended until ${new Date(until).toLocaleDateString()}`);
      user.suspended_until = until;
    } catch { toast.error("Could not suspend"); } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-support-dialog">
      <div className="bg-surface-DEFAULT border border-white/10 p-6 max-w-lg w-full space-y-5" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="label-tiny text-brand-heat text-[10px]">// SUPPORT TOOLKIT</div>
          <div className="font-heading text-xl">{user.name}</div>
          <div className="text-xs text-text-secondary">{user.email}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={impersonate}
            disabled={!!busy || user.role === "super_admin"}
            className="border border-white/15 hover:!border-brand-accent hover:!text-brand-accent px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-40 transition-colors"
            data-testid="support-impersonate"
          >
            {busy === "impersonate" ? "…" : "IMPERSONATE"}
          </button>
          <button
            onClick={forceLogout}
            disabled={!!busy}
            className="border border-white/15 hover:!border-yellow-500 hover:!text-yellow-500 px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-40 transition-colors"
            data-testid="support-force-logout"
          >
            {busy === "logout" ? "…" : "FORCE LOGOUT"}
          </button>
          <button
            onClick={resetPw}
            disabled={!!busy}
            className="border border-white/15 hover:!border-red-500 hover:!text-red-500 px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-40 transition-colors"
            data-testid="support-reset-password"
          >
            {busy === "reset" ? "…" : "RESET PASSWORD"}
          </button>
          <button
            onClick={toggleVerify}
            disabled={!!busy}
            className={`border px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-40 transition-colors ${
              user.is_verified
                ? "border-brand-heat text-brand-heat hover:!bg-brand-heat hover:!text-white"
                : "border-white/15 hover:!border-brand-heat hover:!text-brand-heat"
            }`}
            data-testid="support-verify"
          >
            {busy === "verify" ? "…" : user.is_verified ? "✓ VERIFIED" : "VERIFY COACH"}
          </button>
          <button
            onClick={toggleSuspend}
            disabled={!!busy || user.role === "super_admin"}
            className={`col-span-2 border px-3 py-2 text-xs label-tiny tracking-widest disabled:opacity-40 transition-colors ${
              user.suspended_until
                ? "border-red-500 text-red-500 hover:!bg-red-500 hover:!text-white"
                : "border-white/15 hover:!border-red-500 hover:!text-red-500"
            }`}
            data-testid="support-suspend"
          >
            {busy === "suspend" ? "…" : user.suspended_until ? "LIFT SUSPENSION" : "SUSPEND ACCOUNT"}
          </button>
        </div>

        {newPassword && (
          <div className="border border-brand-heat bg-brand-heat/10 px-3 py-2" data-testid="support-new-password">
            <div className="label-tiny text-[10px] text-brand-heat mb-1">TEMP PASSWORD (SHARE SECURELY)</div>
            <code className="font-mono text-sm select-all">{newPassword}</code>
          </div>
        )}

        <div className="border-t border-white/10 pt-4 space-y-2">
          <div className="label-tiny text-[10px] text-text-secondary">SEND IN-APP MESSAGE (BELL)</div>
          <Input placeholder="Title (required)" value={msgTitle} onChange={(e) => setMsgTitle(e.target.value)} data-testid="support-message-title" />
          <textarea
            placeholder="Body (optional)"
            value={msgBody}
            onChange={(e) => setMsgBody(e.target.value)}
            rows={3}
            className="w-full bg-transparent border border-white/10 text-sm p-2 focus:outline-none focus:border-heat"
            data-testid="support-message-body"
          />
          <button
            onClick={sendMessage}
            disabled={!!busy || !msgTitle.trim()}
            className="btn-kinetic w-full py-2 text-xs label-tiny tracking-widest disabled:opacity-40"
            data-testid="support-send-message"
          >
            {busy === "message" ? "SENDING…" : "SEND MESSAGE"}
          </button>
        </div>

        <button onClick={onClose} className="w-full text-[11px] text-text-secondary hover:!text-white label-tiny tracking-widest" data-testid="support-close">
          CLOSE
        </button>
      </div>
    </div>
  );
};

const ChangeRoleDialog = ({ user, onClose, onSave }) => {
  const [role, setRole] = useState(user.primary_role || "general_client");
  const ROLE_CHOICES = [
    { value: "academy_admin",   label: "Academy Admin" },
    { value: "strength_coach",  label: "Strength Coach" },
    { value: "fitness_trainer", label: "Fitness Trainer" },
    { value: "sports_coach",    label: "Sports Coach" },
    { value: "athlete",         label: "Athlete" },
    { value: "general_client",  label: "General Client" },
    { value: "parent",          label: "Parent / Guardian" },
  ];
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-change-role-dialog"
    >
      <div
        className="bg-white border border-slate-200 shadow-xl max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="label-tiny text-brand-heat text-[10px]">// CHANGE PRIMARY ROLE</div>
          <h3 className="font-heading text-2xl mt-1">{user.name}</h3>
          <p className="text-xs text-slate-500">{user.email}</p>
          <p className="text-[11px] text-slate-600 mt-2">
            Changing primary role reassigns sidebar, dashboard route and access boundaries.
            Coach-family roles automatically enable the coach flag.
          </p>
        </div>
        <div className="space-y-1.5">
          {ROLE_CHOICES.map((r) => {
            const chip = ROLE_CHIP[r.value];
            return (
              <label
                key={r.value}
                className={`flex items-center gap-3 border p-3 cursor-pointer transition ${
                  role === r.value ? "border-brand-heat bg-orange-50" : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`admin-role-choice-${r.value}`}
              >
                <input
                  type="radio"
                  name="primary_role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                  className="accent-brand-heat"
                />
                <span className="text-sm flex-1 text-slate-800">{r.label}</span>
                {chip && <span className={`label-tiny text-[9px] px-2 py-0.5 ${chip.cls}`}>{chip.label}</span>}
              </label>
            );
          })}
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button
            onClick={onClose}
            className="text-xs label-tiny tracking-wider px-4 py-2 border border-slate-300 text-slate-700 hover:border-slate-500"
            data-testid="admin-change-role-cancel"
          >CANCEL</button>
          <button
            onClick={() => onSave(role)}
            disabled={role === user.primary_role}
            className="text-xs label-tiny tracking-wider px-4 py-2 bg-brand-heat text-white hover:bg-brand-heatHover disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-change-role-save"
          >SAVE ROLE</button>
        </div>
      </div>
    </div>
  );
};

const _ACTIVITY_META = {
  invited:        { label: "Admin invited",   cls: "text-brand-accent border-brand-accent/30 bg-brand-accent/5", verb: "invited" },
  resent:         { label: "Invite resent",   cls: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5", verb: "re-invited" },
  revoked:        { label: "Invite revoked",  cls: "text-red-400 border-red-400/30 bg-red-400/5", verb: "revoked invite for" },
  promoted:       { label: "Admin set",       cls: "text-violet-400 border-violet-400/30 bg-violet-400/5", verb: "made admin" },
  admin_joined:   { label: "Admin joined",    cls: "text-amber-400 border-amber-400/30 bg-amber-400/5", verb: "accepted & claimed" },
  member_invited: { label: "Member invited",  cls: "text-cyan-400 border-cyan-400/30 bg-cyan-400/5", verb: "invited a" },
  member_joined:  { label: "Member joined",   cls: "text-blue-400 border-blue-400/30 bg-blue-400/5", verb: "joined as" },
};

const _timeAgo = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
};

const AcademyActivityTab = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | invites | joins | revokes
  const [academy, setAcademy] = useState(null); // {id, name} — per-academy filter
  const [actor, setActor] = useState(null); // {id, name} — per-actor filter
  const load = useCallback((acadId, actId) => {
    const qs = `${acadId ? `&academy_id=${encodeURIComponent(acadId)}` : ""}${actId ? `&actor_id=${encodeURIComponent(actId)}` : ""}`;
    api.get(`/admin/academy-activity?limit=200${qs}`).then(({ data }) => setData(data)).catch(() => toast.error("Failed"));
  }, []);
  useEffect(() => { load(academy?.id, actor?.id); }, [load, academy, actor]);

  const selectAcademy = (id, name) => { setFilter("all"); setActor(null); setAcademy({ id, name }); };
  const selectActor = (id, name) => { if (!id) return; setFilter("all"); setAcademy(null); setActor({ id, name }); };

  if (!data) return <div className="p-10 label-tiny text-text-secondary">Loading activity...</div>;

  const groups = {
    invites: ["invited", "member_invited", "resent"],
    joins: ["admin_joined", "member_joined", "promoted"],
    revokes: ["revoked"],
  };
  const items = (data.items || []).filter((e) => filter === "all" || groups[filter]?.includes(e.event));

  const Stat = ({ id, label, val, color }) => (
    <button
      onClick={() => setFilter(filter === id ? "all" : id)}
      className={`flex-1 border p-4 text-left transition ${filter === id ? "border-brand-accent bg-brand-accent/10" : "border-white/10 hover:border-white/25"}`}
      data-testid={`activity-stat-${id}`}
    >
      <div className={`font-heading text-3xl tabular-nums ${color}`}>{val}</div>
      <div className="label-tiny text-text-secondary mt-1">{label} · 7D</div>
    </button>
  );

  return (
    <div className="space-y-4" data-testid="admin-academy-activity">
      <div className="flex gap-3">
        <Stat id="invites" label="Invites sent" val={data.counts?.invites ?? 0} color="text-brand-accent" />
        <Stat id="joins" label="Joins" val={data.counts?.joins ?? 0} color="text-emerald-400" />
        <Stat id="revokes" label="Revokes" val={data.counts?.revokes ?? 0} color="text-red-400" />
      </div>

      {academy && (
        <div className="flex items-center justify-between border border-brand-accent/40 bg-brand-accent/10 px-4 py-2.5" data-testid="activity-academy-filter">
          <span className="text-sm">
            <span className="label-tiny text-text-secondary">TIMELINE FOR</span>{" "}
            <span className="font-heading text-brand-accent">{academy.name}</span>
          </span>
          <button onClick={() => setAcademy(null)} className="label-tiny text-brand-accent hover:underline inline-flex items-center gap-1" data-testid="activity-academy-clear">
            <X size={11} weight="bold" /> ALL ACADEMIES
          </button>
        </div>
      )}

      {actor && (
        <div className="flex items-center justify-between border border-violet-400/40 bg-violet-400/10 px-4 py-2.5" data-testid="activity-actor-filter">
          <span className="text-sm">
            <span className="label-tiny text-text-secondary">ACTIONS BY</span>{" "}
            <span className="font-heading text-violet-300">{actor.name}</span>
          </span>
          <button onClick={() => setActor(null)} className="label-tiny text-violet-300 hover:underline inline-flex items-center gap-1" data-testid="activity-actor-clear">
            <X size={11} weight="bold" /> EVERYONE
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="label-tiny text-text-secondary">
          {filter === "all" ? "ALL ACTIVITY" : `FILTERED: ${filter.toUpperCase()}`} · {items.length}
        </span>
        <div className="flex items-center gap-2">
          {filter !== "all" && (
            <button onClick={() => setFilter("all")} className="label-tiny text-brand-accent hover:underline" data-testid="activity-clear-filter">CLEAR</button>
          )}
          <button onClick={() => load(academy?.id, actor?.id)} className="label-tiny text-text-secondary hover:text-white" data-testid="activity-refresh">REFRESH</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="activity-empty">
          No academy activity yet.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="activity-feed">
          {items.map((e) => {
            const m = _ACTIVITY_META[e.event] || { label: e.event, cls: "text-text-secondary border-white/10", verb: e.event };
            return (
              <div key={e.activity_id} className="p-3 flex items-center gap-3" data-testid={`activity-row-${e.activity_id}`}>
                <span className={`label-tiny text-[9px] px-2 py-1 rounded border shrink-0 ${m.cls}`}>{m.label}</span>
                <div className="flex-1 min-w-0 text-sm">
                  <button
                    onClick={() => selectActor(e.actor_id, e.actor_name || e.actor_email)}
                    className="font-medium hover:text-violet-300 hover:underline"
                    data-testid={`activity-actor-link-${e.activity_id}`}
                    title="Show everything this person has done"
                  >
                    {e.actor_name || e.actor_email || "Someone"}
                  </button>
                  <span className="text-text-secondary"> {m.verb} </span>
                  {e.target_email && <span className="text-white/90">{e.target_email}</span>}
                  {e.role && <span className="text-text-secondary"> ({e.role.replace(/_/g, " ")})</span>}
                  <span className="text-text-secondary"> · </span>
                  <button
                    onClick={() => e.academy_id && selectAcademy(e.academy_id, e.academy_name)}
                    className="text-brand-accent hover:underline"
                    data-testid={`activity-academy-link-${e.activity_id}`}
                    title="Show only this academy's activity"
                  >
                    {e.academy_name || "—"}
                  </button>
                </div>
                <span className="label-tiny text-[10px] text-text-secondary shrink-0">{_timeAgo(e.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PendingInviteControls = ({ academy, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(academy.pending_admin_email || academy.pending_invite?.invite_email || "");
  const hasInvite = !!academy.pending_invite;
  const link = academy.pending_invite?.code ? `${window.location.origin}/academy-join/${academy.pending_invite.code}` : null;

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Invite link copied"); };

  const resend = async () => {
    if (!email.trim()) { toast.error("Enter an admin email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/academies/${academy.academy_id}/invite/resend`, {
        admin_email: email.trim(), send_email: true, base_url: window.location.origin,
      });
      copy(data.link);
      if (data.email_sent) {
        toast.success(hasInvite ? "Invitation resent & emailed — link copied" : "Invitation sent & emailed — link copied");
      } else {
        toast.success(hasInvite ? "New invite link copied" : "Invite link copied");
        if (data.email_error) toast.warning(data.email_error, { duration: 8000 });
      }
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Cancel this pending admin invitation? The current link will stop working.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/academies/${academy.academy_id}/invite/revoke`);
      toast.success("Invitation cancelled");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (hasInvite) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2" data-testid={`pending-controls-${academy.academy_id}`}>
        <button onClick={() => copy(link)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-brand-accent hover:underline disabled:opacity-50" data-testid={`admin-academy-copy-pending-${academy.academy_id}`}>
          <Copy size={11} weight="bold" /> Copy link
        </button>
        <button onClick={resend} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-emerald-400 hover:underline disabled:opacity-50" data-testid={`admin-academy-resend-${academy.academy_id}`}>
          <PaperPlaneTilt size={11} weight="bold" /> {busy ? "..." : "Resend"}
        </button>
        <button onClick={revoke} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-red-400 hover:underline disabled:opacity-50" data-testid={`admin-academy-revoke-${academy.academy_id}`}>
          <Trash size={11} weight="bold" /> Revoke
        </button>
      </div>
    );
  }
  // Invite was revoked — offer a fresh invite (optionally to a corrected email).
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`reinvite-${academy.academy_id}`}>
      <span className="text-[11px] text-amber-300">No active invite —</span>
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="admin email"
        className="bg-black/30 border border-white/10 px-2 py-1 text-[11px] text-white/90 outline-none"
        data-testid={`reinvite-email-${academy.academy_id}`}
      />
      <button onClick={resend} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-brand-accent hover:underline disabled:opacity-50" data-testid={`reinvite-send-${academy.academy_id}`}>
        <PaperPlaneTilt size={11} weight="bold" /> {busy ? "SENDING..." : "Send invite"}
      </button>
    </div>
  );
};

const AcademiesTab = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [mode, setMode] = useState("invite"); // invite | promote
  const [adminEmail, setAdminEmail] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/academies").then(({ data }) => setRows(data)).catch(() => toast.error("Failed"));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/email/status").then(({ data }) => setEmailStatus(data)).catch(() => {}); }, []);

  const searchUsers = async (q) => {
    setUserSearch(q);
    setSelectedUser(null);
    if (!q.trim() || q.trim().length < 2) { setUserResults([]); return; }
    try {
      const { data } = await api.get(`/admin/users?search=${encodeURIComponent(q.trim())}&limit=8`);
      setUserResults(data.items || []);
    } catch { setUserResults([]); }
  };

  const reset = () => {
    setName(""); setDesc(""); setMode("invite"); setAdminEmail("");
    setSendEmail(true); setUserSearch(""); setUserResults([]); setSelectedUser(null); setResult(null);
  };

  const create = async () => {
    if (!name.trim()) { toast.error("Academy name is required"); return; }
    if (mode === "invite" && !adminEmail.trim()) { toast.error("Admin email is required"); return; }
    if (mode === "promote" && !selectedUser) { toast.error("Select a user to promote"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/academies", {
        name: name.trim(),
        description: desc.trim() || null,
        assign_mode: mode,
        admin_email: mode === "invite" ? adminEmail.trim() : null,
        admin_user_id: mode === "promote" ? selectedUser.user_id : null,
        send_email: sendEmail,
        base_url: window.location.origin,
      });
      setResult(data);
      toast.success(
        mode === "invite"
          ? "Academy created — invitation ready"
          : `Academy created — ${selectedUser.name || selectedUser.email} is now admin`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create academy");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const del = async (a) => {
    if (!window.confirm(`Delete academy "${a.name}"? All memberships will be removed.`)) return;
    try {
      await api.delete(`/admin/academies/${a.academy_id}`);
      toast.success("Academy deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-academies">
      {emailStatus && (emailStatus.sandbox || !emailStatus.configured) && (
        <div className="relative z-10 flex items-start gap-2.5 border border-amber-400/40 bg-[#241a07] px-3.5 py-2.5" data-testid="admin-email-status-banner">
          <Warning size={16} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-[11px] leading-relaxed text-amber-50">
            <span className="label-tiny tracking-widest text-amber-300">INVITE EMAILS: SANDBOX MODE</span>
            <p className="mt-1 text-amber-50">{emailStatus.message}</p>
            <p className="mt-1 text-amber-100/90">
              Current sender: <code className="text-white">{emailStatus.sender}</code> ·
              {" "}Invite links below always work regardless of email.
            </p>
          </div>
        </div>
      )}
      {emailStatus && !emailStatus.sandbox && emailStatus.configured && (
        <div className="relative z-10 flex items-center gap-2 border border-emerald-400/30 bg-[#06231a] px-3.5 py-2" data-testid="admin-email-status-banner">
          <Envelope size={15} weight="fill" className="text-emerald-400 shrink-0" />
          <p className="text-[11px] text-emerald-50">
            Verified sender active — invite emails reach real admins. Sender: <code className="text-white">{emailStatus.sender}</code>
          </p>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => { if (open) reset(); setOpen(!open); }}
          className="inline-flex items-center gap-1.5 bg-brand-accent text-surface-base px-3 py-2 text-[11px] label-tiny tracking-widest hover:opacity-90 transition"
          data-testid="admin-create-academy-toggle"
        >
          <Plus size={13} weight="bold" /> {open ? "CLOSE" : "CREATE ACADEMY"}
        </button>
        <button
          onClick={() => downloadCsv("/admin/academies/export.csv", "edge-academies.csv")}
          className="inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors"
          data-testid="admin-academies-export-csv"
        >
          <DownloadSimple size={12} weight="bold" /> EXPORT CSV
        </button>
      </div>

      {/* Create panel */}
      {open && (
        <div className="border border-brand-accent/30 bg-white/[0.02] p-5 space-y-4" data-testid="admin-create-academy-panel">
          {result ? (
            <div className="space-y-3" data-testid="admin-create-academy-result">
              <div className="flex items-center gap-2 text-emerald-400">
                <Buildings size={18} weight="duotone" />
                <span className="font-heading text-lg">{result.academy?.name} created</span>
              </div>
              {result.mode === "invite" ? (
                <>
                  <p className="text-xs text-text-secondary">
                    Invitation created for <strong className="text-white">{result.invite?.email}</strong>.
                    They open this link to accept and claim the academy as its admin:
                    {result.email_sent ? " An email was also sent." : ""}
                  </p>
                  {!result.email_sent && result.email_error && (
                    <p className="flex items-start gap-1.5 text-[11px] text-amber-300" data-testid="admin-academy-invite-email-error">
                      <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> {result.email_error}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={result.invite?.link || ""}
                      className="flex-1 bg-black/30 border border-white/10 px-3 py-2 text-xs text-white/90 outline-none"
                      data-testid="admin-academy-invite-link"
                    />
                    <button
                      onClick={() => copy(result.invite?.link)}
                      className="inline-flex items-center gap-1 border border-white/15 hover:border-brand-accent hover:text-brand-accent px-3 py-2 text-[11px] label-tiny"
                      data-testid="admin-academy-copy-link"
                    >
                      <Copy size={12} weight="bold" /> COPY
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-text-secondary">
                  <strong className="text-white">{result.assigned_admin?.name || result.assigned_admin?.email}</strong> is
                  now the admin of this academy and can invite coaches and athletes.
                  {result.email_sent ? " A heads-up email was sent." : ""}
                  {!result.email_sent && result.email_error && (
                    <span className="mt-1 flex items-start gap-1.5 text-amber-300" data-testid="admin-academy-promote-email-error">
                      <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> {result.email_error}
                    </span>
                  )}
                </p>
              )}
              <button
                onClick={() => { reset(); }}
                className="text-[11px] label-tiny text-brand-accent hover:underline"
                data-testid="admin-create-another-academy"
              >
                + CREATE ANOTHER
              </button>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-tiny text-text-secondary">ACADEMY NAME</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Peak Performance Academy" maxLength={100} data-testid="admin-academy-name" />
                </div>
                <div>
                  <label className="label-tiny text-text-secondary">DESCRIPTION (OPTIONAL)</label>
                  <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What's this academy about?" data-testid="admin-academy-desc" />
                </div>
              </div>

              {/* Assign-admin mode */}
              <div>
                <label className="label-tiny text-text-secondary mb-1.5 block">ASSIGN ADMIN</label>
                <div className="flex gap-2">
                  {[["invite", "Invite by email"], ["promote", "Promote existing user"]].map(([val, lbl]) => (
                    <button
                      key={val}
                      onClick={() => setMode(val)}
                      className={`flex-1 px-3 py-2 text-[11px] label-tiny tracking-widest border transition ${
                        mode === val ? "border-brand-accent bg-brand-accent/10 text-brand-accent" : "border-white/10 text-text-secondary hover:border-white/25"
                      }`}
                      data-testid={`admin-academy-mode-${val}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {mode === "invite" ? (
                <div>
                  <label className="label-tiny text-text-secondary">ADMIN EMAIL</label>
                  <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@theiracademy.com" data-testid="admin-academy-admin-email" />
                  <p className="text-[11px] text-text-secondary mt-1">They'll get an invite link (and an email) to accept and set up their academy.</p>
                </div>
              ) : (
                <div>
                  <label className="label-tiny text-text-secondary">FIND A USER</label>
                  <Input value={userSearch} onChange={(e) => searchUsers(e.target.value)} placeholder="Search by name or email" data-testid="admin-academy-user-search" />
                  {selectedUser ? (
                    <div className="mt-2 flex items-center justify-between border border-brand-accent/40 bg-brand-accent/10 px-3 py-2" data-testid="admin-academy-selected-user">
                      <span className="text-xs text-white">{selectedUser.name || "—"} · {selectedUser.email}</span>
                      <button onClick={() => { setSelectedUser(null); setUserSearch(""); }} className="text-text-secondary hover:text-white"><Trash size={13} /></button>
                    </div>
                  ) : userResults.length > 0 && (
                    <div className="mt-2 border border-white/10 divide-y divide-white/5 max-h-48 overflow-y-auto">
                      {userResults.map((u) => (
                        <button
                          key={u.user_id}
                          onClick={() => { setSelectedUser(u); setUserResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-white/5 transition"
                          data-testid={`admin-academy-user-option-${u.user_id}`}
                        >
                          <div className="text-xs text-white">{u.name || "—"}</div>
                          <div className="text-[11px] text-text-secondary">{u.email} · {u.primary_role}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer" data-testid="admin-academy-send-email">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                Send a notification email {mode === "invite" ? "with the invite link" : "to the new admin"}
              </label>

              <button
                onClick={create}
                disabled={busy}
                className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-5 py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60"
                data-testid="admin-academy-create-submit"
              >
                <Buildings size={14} weight="duotone" /> {busy ? "CREATING..." : "CREATE ACADEMY"}
              </button>
            </>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="admin-academies-empty">
          No academies yet.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((a) => {
            const pending = a.status === "pending_admin";
            return (
              <div key={a.academy_id} className="p-4 flex items-center gap-4" data-testid={`admin-academy-row-${a.academy_id}`}>
                <Buildings size={22} className="text-brand-accent" weight="duotone" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <span
                      className={`label-tiny text-[9px] px-1.5 py-0.5 rounded ${pending ? "bg-amber-400/15 text-amber-300 border border-amber-400/30" : "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"}`}
                      data-testid={`admin-academy-status-${a.academy_id}`}
                    >
                      {pending ? "PENDING ADMIN" : "ACTIVE"}
                    </span>
                  </div>
                  <div className="text-[11px] text-text-secondary truncate">
                    /a/{a.slug} · {pending ? `invited: ${a.pending_admin_email || a.pending_invite?.invite_email || "—"}` : `owner: ${a.owner.email || "—"}`}
                  </div>
                  <div className="text-[11px] text-text-secondary">
                    {a.counts.total} members ({a.counts.admin} admin · {a.counts.coach} coach · {a.counts.athlete} athletes) · plan: {a.plan?.toUpperCase()}
                  </div>
                  {pending && <PendingInviteControls academy={a} onChange={load} />}
                </div>
                <button
                  onClick={() => del(a)}
                  className="p-2 text-text-secondary hover:text-red-500 transition"
                  data-testid={`admin-delete-academy-${a.academy_id}`}
                  aria-label="Delete academy"
                >
                  <Trash size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LEAD_STATUS_META = {
  new: { label: "New", cls: "bg-brand-accent/20 text-brand-accent border-brand-accent/40" },
  contacted: { label: "Contacted", cls: "bg-heat/20 text-heat border-heat/40" },
  closed: { label: "Closed", cls: "bg-white/10 text-white/50 border-white/20" },
};

const LeadsTab = () => {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [admins, setAdmins] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/demo-requests${params}`);
      setData(data);
    } catch {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/system-admins").then(({ data }) => setAdmins(data.items || [])).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/demo-requests/${id}`, { status });
      toast.success(`Marked ${LEAD_STATUS_META[status]?.label || status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/demo-requests/${id}`);
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const assignOwner = async (id, owner_id) => {
    try {
      await api.patch(`/admin/demo-requests/${id}/owner`, { owner_id: owner_id || null });
      toast.success(owner_id ? "Owner assigned" : "Owner cleared");
      load();
    } catch {
      toast.error("Could not assign owner");
    }
  };

  const addNote = async (id, text) => {
    try {
      await api.post(`/admin/demo-requests/${id}/notes`, { text });
      load();
      return true;
    } catch {
      toast.error("Could not add note");
      return false;
    }
  };

  const deleteNote = async (id, note_id) => {
    try {
      await api.delete(`/admin/demo-requests/${id}/notes/${note_id}`);
      load();
    } catch {
      toast.error("Could not delete note");
    }
  };

  const counts = data.counts || {};

  return (
    <div className="space-y-4" data-testid="admin-leads">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="lead-filter-all"
          className={`px-4 py-1.5 text-xs label-tiny tracking-widest border transition-colors ${filter === "" ? "bg-brand-accent text-white border-brand-accent" : "border-white/20 text-text-secondary hover:border-brand-accent"}`}
        >
          ALL · {data.total || 0}
        </button>
        {["new", "contacted", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`lead-filter-${s}`}
            className={`px-4 py-1.5 text-xs label-tiny tracking-widest border transition-colors ${filter === s ? "bg-brand-accent text-white border-brand-accent" : "border-white/20 text-text-secondary hover:border-brand-accent"}`}
          >
            {LEAD_STATUS_META[s].label.toUpperCase()} · {counts[s] || 0}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary" data-testid="admin-leads-loading">Loading...</div>
      ) : data.items.length === 0 ? (
        <div className="border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="admin-leads-empty">
          <Envelope size={40} weight="duotone" className="text-text-secondary mx-auto mb-3" />
          <p className="text-sm text-text-secondary">No demo requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => (
            <LeadCard
              key={r.request_id}
              lead={r}
              admins={admins}
              onStatus={setStatus}
              onRemove={remove}
              onAssign={assignOwner}
              onAddNote={addNote}
              onDeleteNote={deleteNote}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const LeadCard = ({ lead: r, admins, onStatus, onRemove, onAssign, onAddNote, onDeleteNote }) => {
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const meta = LEAD_STATUS_META[r.status] || LEAD_STATUS_META.new;
  const notes = r.notes || [];
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  const submitNote = async (e) => {
    e.preventDefault();
    const t = noteText.trim();
    if (!t) return;
    setSavingNote(true);
    const ok = await onAddNote(r.request_id, t);
    if (ok) setNoteText("");
    setSavingNote(false);
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={`lead-row-${r.request_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className="font-heading text-lg">{r.name}</span>
            <span className={`label-tiny px-2 py-0.5 border ${meta.cls}`} data-testid={`lead-status-${r.request_id}`}>
              {meta.label}
            </span>
            {r.owner_name && (
              <span className="label-tiny px-2 py-0.5 border border-brand-accent/40 bg-brand-accent/10 text-brand-accent" data-testid={`lead-owner-${r.request_id}`}>
                OWNER: {r.owner_name}
              </span>
            )}
          </div>
          <div className="text-sm text-text-secondary flex flex-wrap gap-x-4 gap-y-1">
            <a href={`mailto:${r.email}`} className="text-brand-accent hover:underline">{r.email}</a>
            {r.organization && <span>{r.organization}</span>}
            {r.team_size && <span>{r.team_size} athletes</span>}
            {r.phone && <span>{r.phone}</span>}
          </div>
          {r.message && <p className="text-sm text-white/80 mt-2 max-w-2xl">{r.message}</p>}
          <div className="text-[11px] label-tiny text-text-secondary/70 mt-2">{fmt(r.created_at)}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {r.status !== "contacted" && (
            <button onClick={() => onStatus(r.request_id, "contacted")} data-testid={`lead-mark-contacted-${r.request_id}`}
              className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-heat/50 text-heat hover:bg-heat hover:text-surface-base transition-colors">
              CONTACTED
            </button>
          )}
          {r.status !== "closed" && (
            <button onClick={() => onStatus(r.request_id, "closed")} data-testid={`lead-mark-closed-${r.request_id}`}
              className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-white/40 transition-colors">
              CLOSE
            </button>
          )}
          {r.status !== "new" && (
            <button onClick={() => onStatus(r.request_id, "new")} data-testid={`lead-mark-new-${r.request_id}`}
              className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-brand-accent transition-colors">
              REOPEN
            </button>
          )}
          <button onClick={() => onRemove(r.request_id)} data-testid={`lead-delete-${r.request_id}`}
            className="p-2 text-text-secondary hover:text-red-400 transition-colors" title="Delete">
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* Owner assignment + internal notes */}
      <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="label-tiny text-text-secondary">ASSIGN OWNER</label>
          <select
            value={r.owner_id || ""}
            onChange={(e) => onAssign(r.request_id, e.target.value)}
            data-testid={`lead-owner-select-${r.request_id}`}
            className="bg-white/[0.04] border border-white/15 px-3 py-1.5 text-xs text-white focus:border-brand-accent outline-none"
          >
            <option value="" className="bg-surface-base">Unassigned</option>
            {admins.map((a) => (
              <option key={a.user_id} value={a.user_id} className="bg-surface-base">
                {a.name || a.email}
              </option>
            ))}
          </select>
        </div>

        {notes.length > 0 && (
          <div className="space-y-2" data-testid={`lead-notes-${r.request_id}`}>
            {notes.map((n) => (
              <div key={n.note_id} className="flex items-start justify-between gap-3 bg-white/[0.03] px-3 py-2 border-l-2 border-brand-accent/40">
                <div className="min-w-0">
                  <p className="text-sm text-white/90">{n.text}</p>
                  <div className="text-[11px] label-tiny text-text-secondary/70 mt-0.5">
                    {n.author_name} · {fmt(n.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => onDeleteNote(r.request_id, n.note_id)}
                  data-testid={`lead-note-delete-${n.note_id}`}
                  className="text-text-secondary hover:text-red-400 transition-colors flex-shrink-0" title="Delete note">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={submitNote} className="flex items-center gap-2">
          <input
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add an internal note…"
            data-testid={`lead-note-input-${r.request_id}`}
            className="flex-1 bg-white/[0.04] border border-white/15 px-3 py-1.5 text-sm text-white placeholder-white/30 focus:border-brand-accent outline-none"
          />
          <button
            type="submit"
            disabled={savingNote || !noteText.trim()}
            data-testid={`lead-note-add-${r.request_id}`}
            className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white transition-colors disabled:opacity-40">
            {savingNote ? "…" : "ADD NOTE"}
          </button>
        </form>
      </div>
    </div>
  );
};


const PaymentsTab = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments?limit=100").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="admin-payments">
      <div className="flex items-center justify-end">
        <button
          onClick={() => downloadCsv("/admin/payments/export.csv", "edge-payments.csv")}
          className="inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors"
          data-testid="admin-payments-export-csv"
        >
          <DownloadSimple size={12} weight="bold" /> EXPORT CSV
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="admin-payments-empty">
          No payments yet.
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {rows.map((r) => (
            <div key={r.order_id} className="p-4 flex items-center gap-4" data-testid={`admin-payment-row-${r.order_id}`}>
              <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 ${
                r.status === "paid" ? "bg-green-500/15 text-green-600" : "bg-white/5 text-text-secondary"
              }`}>
                {r.status.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.user.name || r.user.email || r.user_id}</div>
                <div className="text-[11px] text-text-secondary truncate">
                  {r.package_id} · {r.order_id}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-heading text-brand-accent tabular-nums">₹{r.amount_inr.toLocaleString()}</div>
                <div className="text-[10px] text-text-secondary">
                  {new Date(r.paid_at || r.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AUDIT_ACTIONS = [
  { value: "", label: "ALL ACTIONS" },
  { value: "update_user", label: "UPDATE USER" },
  { value: "delete_user", label: "DELETE USER" },
  { value: "delete_academy", label: "DELETE ACADEMY" },
  { value: "export_csv", label: "EXPORT CSV" },
];

const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [days, setDays] = useState("30");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (action) params.set("action", action);
      if (adminEmail.trim()) params.set("admin_email", adminEmail.trim());
      if (days && days !== "all") {
        const since = new Date(Date.now() - parseInt(days, 10) * 86400000).toISOString();
        params.set("since", since);
      }
      const { data } = await api.get(`/admin/audit?${params.toString()}`);
      setRows(data?.items || []);
    } catch {
      toast.error("Could not load audit log");
    } finally {
      setBusy(false);
    }
  }, [action, adminEmail, days]);

  useEffect(() => { load(); }, [load]);

  const actionLabel = (a) => (AUDIT_ACTIONS.find((x) => x.value === a)?.label || a.toUpperCase());
  const actionTone = (a) => {
    if (a === "delete_user" || a === "delete_academy") return "bg-red-500/15 text-red-600";
    if (a === "update_user") return "bg-yellow-500/15 text-yellow-600";
    if (a === "export_csv") return "bg-brand-heat/15 text-brand-heat";
    return "bg-white/5 text-text-secondary";
  };

  return (
    <div className="space-y-4" data-testid="admin-audit">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="bg-transparent border border-white/10 text-xs px-3 py-2 text-white focus:outline-none focus:border-heat"
          data-testid="admin-audit-action-filter"
        >
          {AUDIT_ACTIONS.map((a) => (
            <option key={a.value || "all"} value={a.value} className="bg-surface-DEFAULT">{a.label}</option>
          ))}
        </select>
        <Input
          placeholder="Admin email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          className="max-w-[220px]"
          data-testid="admin-audit-email-filter"
        />
        <select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="bg-transparent border border-white/10 text-xs px-3 py-2 text-white focus:outline-none focus:border-heat"
          data-testid="admin-audit-days-filter"
        >
          <option value="1" className="bg-surface-DEFAULT">LAST 24H</option>
          <option value="7" className="bg-surface-DEFAULT">LAST 7 DAYS</option>
          <option value="30" className="bg-surface-DEFAULT">LAST 30 DAYS</option>
          <option value="90" className="bg-surface-DEFAULT">LAST 90 DAYS</option>
          <option value="all" className="bg-surface-DEFAULT">ALL TIME</option>
        </select>
        <button
          onClick={() => downloadCsv("/admin/audit/export.csv", "edge-audit.csv")}
          className="ml-auto inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors"
          data-testid="admin-audit-export-csv"
        >
          <DownloadSimple size={12} weight="bold" /> EXPORT CSV
        </button>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-audit-list">
        {rows.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm" data-testid="admin-audit-empty">
            No matching audit entries.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.log_id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-audit-row-${r.log_id}`}>
            <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 ${actionTone(r.action)}`}>
              {actionLabel(r.action)}
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-sm">
                <span className="font-medium">{r.admin_name || r.admin_email}</span>
                {r.target_type && (
                  <> {" → "}<span className="text-text-secondary">{r.target_type}{r.target_id ? ` · ${r.target_id.slice(0, 12)}…` : ""}</span></>
                )}
              </div>
              {Object.keys(r.meta || {}).length > 0 && (
                <div className="text-[10px] text-text-secondary font-mono truncate max-w-full mt-0.5">
                  {JSON.stringify(r.meta)}
                </div>
              )}
            </div>
            <div className="text-[10px] text-text-secondary">
              {new Date(r.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const CONSENT_EVENTS = [
  { value: "", label: "ALL EVENTS" },
  { value: "requested", label: "REQUESTED" },
  { value: "re_requested", label: "RE-REQUESTED" },
  { value: "granted", label: "GRANTED" },
  { value: "declined", label: "DECLINED" },
  { value: "expired", label: "EXPIRED" },
];

const ConsentAuditTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [event, setEvent] = useState("");
  const [athleteEmail, setAthleteEmail] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/admin/consent-summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (event) params.set("event", event);
      if (athleteEmail.trim()) params.set("athlete_email", athleteEmail.trim());
      const { data } = await api.get(`/admin/consent-audit?${params.toString()}`);
      setRows(data?.items || []);
    } catch {
      toast.error("Could not load consent log");
    } finally {
      setBusy(false);
    }
  }, [event, athleteEmail]);

  useEffect(() => { load(); }, [load]);

  const eventTone = (e) => {
    if (e === "granted") return "bg-green-500/15 text-green-500";
    if (e === "declined") return "bg-red-500/15 text-red-500";
    if (e === "expired") return "bg-white/10 text-text-secondary";
    if (e === "re_requested") return "bg-yellow-500/15 text-yellow-600";
    return "bg-brand-heat/15 text-brand-heat";
  };

  return (
    <div className="space-y-4" data-testid="admin-consent-audit">
      <p className="text-xs text-text-secondary max-w-2xl">
        Immutable, timestamped record of every under-18 guardian-consent event — who requested, who granted
        or declined, and when. For compliance review and export.
      </p>
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-consent-summary">
          {[
            { k: "pending", label: "PENDING", tone: "text-brand-heat" },
            { k: "granted", label: "GRANTED", tone: "text-green-500" },
            { k: "declined", label: "DECLINED", tone: "text-red-500" },
            { k: "expired", label: "EXPIRED", tone: "text-text-secondary" },
          ].map((s) => (
            <div key={s.k} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`consent-stat-${s.k}`}>
              <div className={`font-heading text-3xl leading-none ${s.tone}`}>{summary[s.k] ?? 0}</div>
              <div className="label-tiny text-text-secondary mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          className="bg-transparent border border-white/10 text-xs px-3 py-2 text-white focus:outline-none focus:border-heat"
          data-testid="admin-consent-event-filter"
        >
          {CONSENT_EVENTS.map((a) => (
            <option key={a.value || "all"} value={a.value} className="bg-surface-DEFAULT">{a.label}</option>
          ))}
        </select>
        <Input
          placeholder="Athlete email"
          value={athleteEmail}
          onChange={(e) => setAthleteEmail(e.target.value)}
          className="max-w-[220px]"
          data-testid="admin-consent-email-filter"
        />
        <button
          onClick={() => downloadCsv("/admin/consent-audit/export.csv", "edge-consent-audit.csv")}
          className="ml-auto inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors"
          data-testid="admin-consent-export-csv"
        >
          <DownloadSimple size={12} weight="bold" /> EXPORT CSV
        </button>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-consent-list">
        {rows.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm" data-testid="admin-consent-empty">
            No consent events recorded yet.
          </div>
        )}
        {rows.map((r) => (
          <div key={r.audit_id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-consent-row-${r.audit_id}`}>
            <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 ${eventTone(r.event)}`}>
              {(r.event || "").replace("_", " ").toUpperCase()}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-sm">
                <span className="font-medium">{r.athlete_name || r.athlete_email || "Athlete"}</span>
                {r.age_at_event != null && <span className="text-text-secondary"> · age {r.age_at_event}</span>}
                {r.guardian_email && (
                  <> {" ← "}<span className="text-text-secondary">{r.guardian_name || r.guardian_email}</span></>
                )}
              </div>
              <div className="text-[10px] text-text-secondary font-mono truncate max-w-full mt-0.5">
                {r.athlete_email}{r.guardian_email ? ` · guardian: ${r.guardian_email}` : ""}
              </div>
            </div>
            <div className="text-[10px] text-text-secondary">
              {new Date(r.created_at).toLocaleString()}
            </div>
            {r.event === "granted" && (
              <button
                onClick={() => downloadFile(`/admin/consent-audit/${r.audit_id}/receipt.pdf`, "edge-training-consent-record.pdf", "application/pdf")}
                className="inline-flex items-center gap-1 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-2.5 py-1.5 text-[10px] label-tiny tracking-widest transition-colors"
                data-testid={`admin-consent-pdf-${r.audit_id}`}
                title="Download consent record PDF"
              >
                <DownloadSimple size={11} weight="bold" /> PDF
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};



const FlagsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/feature-flags");
      setRows(data?.items || []);
    } catch {
      toast.error("Could not load feature flags");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = async (key, enabled, description) => {
    try {
      await api.put("/admin/feature-flags", { key, enabled, description: description || "" });
      load();
    } catch {
      toast.error("Could not save flag");
    }
  };

  const addNew = async () => {
    if (!newKey.trim()) { toast.error("Key required"); return; }
    await upsert(newKey, false, newDesc);
    setNewKey(""); setNewDesc("");
    toast.success("Flag created — flip the switch to enable");
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete flag "${key}"?`)) return;
    try {
      await api.delete(`/admin/feature-flags/${encodeURIComponent(key)}`);
      load();
    } catch {
      toast.error("Could not delete flag");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-flags">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-text-secondary">CREATE NEW FLAG</div>
        <div className="flex gap-2 flex-wrap">
          <Input
            placeholder="flag_key_snake_case"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            className="max-w-[260px]"
            data-testid="admin-flag-new-key"
          />
          <Input
            placeholder="What does this flag control?"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            className="flex-1 min-w-[200px]"
            data-testid="admin-flag-new-desc"
          />
          <button
            onClick={addNew}
            disabled={!newKey.trim()}
            className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="admin-flag-add"
          >
            <Plus size={12} weight="bold" /> ADD
          </button>
        </div>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-flags-list">
        {rows.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm" data-testid="admin-flags-empty">
            No feature flags yet. Add one above.
          </div>
        )}
        {rows.map((f) => (
          <div key={f.key} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-flag-row-${f.key}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <code className="font-mono text-sm">{f.key}</code>
                <span className={`label-tiny text-[9px] px-2 py-0.5 ${f.enabled ? "bg-green-500/15 text-green-600" : "bg-white/5 text-text-secondary"}`}>
                  {f.enabled ? "ON" : "OFF"}
                </span>
              </div>
              {f.description && (
                <div className="text-[11px] text-text-secondary mt-0.5">{f.description}</div>
              )}
              {f.updated_by && (
                <div className="text-[10px] text-text-secondary/70 mt-0.5">
                  Last updated by {f.updated_by} · {new Date(f.updated_at).toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={() => upsert(f.key, !f.enabled, f.description)}
              className={`px-3 py-1.5 text-[11px] label-tiny tracking-widest border transition-colors ${
                f.enabled
                  ? "border-green-500 text-green-500 hover:!bg-green-500 hover:!text-surface-base"
                  : "border-white/20 text-text-secondary hover:!border-heat hover:!text-heat"
              }`}
              data-testid={`admin-flag-toggle-${f.key}`}
            >
              {f.enabled ? "TURN OFF" : "TURN ON"}
            </button>
            <button
              onClick={() => remove(f.key)}
              className="p-1.5 text-text-secondary hover:!text-red-500 transition-colors"
              data-testid={`admin-flag-delete-${f.key}`}
              aria-label={`Delete ${f.key}`}
            >
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const HealthCard = ({ title, value, sub }) => (
  <div className="border border-white/10 p-4">
    <div className="label-tiny text-[10px] text-text-secondary mb-1">{title}</div>
    <div className="font-heading text-2xl tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-text-secondary mt-0.5">{sub}</div>}
  </div>
);

const HealthTab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/health");
      setData(data);
    } catch {
      toast.error("Could not load health snapshot");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  if (!data && busy) return <div className="label-tiny text-text-secondary">Loading health...</div>;
  if (!data) return null;

  const uptimeMs = new Date(data.server_time).getTime() - new Date(data.app_started_at).getTime();
  const uptimeMin = Math.floor(uptimeMs / 60000);
  const uptimeH = Math.floor(uptimeMin / 60);
  const uptimeStr = uptimeH > 0 ? `${uptimeH}h ${uptimeMin % 60}m` : `${uptimeMin}m`;

  return (
    <div className="space-y-4" data-testid="admin-health">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tiny text-[10px] text-text-secondary">// LAST REFRESHED</div>
          <div className="text-sm">{new Date(data.server_time).toLocaleTimeString()}</div>
        </div>
        <button
          onClick={load}
          className="text-[11px] label-tiny tracking-widest border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-3 py-1.5 transition-colors"
          data-testid="admin-health-refresh"
        >
          REFRESH
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthCard title="UPTIME" value={uptimeStr} sub={`Started ${new Date(data.app_started_at).toLocaleString()}`} />
        <HealthCard title="SCHEDULER" value={data.quiet_hours_scheduler?.toUpperCase() || "OFF"} sub="Quiet-hours digest flush" />
        <HealthCard title="USERS" value={data.users.total.toLocaleString()} sub={`+${data.users.new_24h} in 24h`} />
        <HealthCard title="SESSIONS" value={data.sessions.active.toLocaleString()} sub="Legacy DB sessions" />
        <HealthCard title="PUSH SUBS" value={data.push.subscriptions.toLocaleString()} sub="Browser push endpoints" />
        <HealthCard title="NOTIFICATIONS" value={data.notifications.last_24h.toLocaleString()} sub={`Total ${data.notifications.total.toLocaleString()} · deferred ${data.notifications.deferred}`} />
        <HealthCard title="AUDIT / HR" value={data.audit.last_hour.toLocaleString()} sub="Admin actions last hour" />
        <HealthCard title="WORKOUTS 24H" value={data.workouts.logged_24h.toLocaleString()} sub="Athlete logs today" />
        <HealthCard title="FEATURE FLAGS" value={data.feature_flags.total} sub="Toggles configured" />
        <HealthCard title="POSTS" value={data.content.posts.total.toLocaleString()} sub={`${data.content.posts.hidden} hidden`} />
        <HealthCard title="REVIEWS" value={data.content.reviews.total.toLocaleString()} sub={`${data.content.reviews.hidden} hidden`} />
      </div>
    </div>
  );
};

const ModerationTab = () => {
  const [target, setTarget] = useState("posts"); // posts | reviews
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/moderation/${target}?only_flagged=${onlyFlagged}&limit=100`);
      setRows(data?.items || []);
    } catch {
      toast.error("Could not load content");
    } finally {
      setBusy(false);
    }
  }, [target, onlyFlagged]);
  useEffect(() => { load(); }, [load]);

  const toggleHide = async (id, current) => {
    try {
      await api.patch(`/admin/moderation/${target}/${id}`, { hidden: !current });
      toast.success(current ? "Restored" : "Hidden from feed");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const purge = async (id) => {
    if (!window.confirm("Permanently delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/moderation/${target}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-moderation">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="bg-transparent border border-white/10 text-xs px-3 py-2 text-white focus:outline-none focus:border-heat"
          data-testid="admin-moderation-target"
        >
          <option value="posts" className="bg-surface-DEFAULT">SOCIAL POSTS</option>
          <option value="reviews" className="bg-surface-DEFAULT">REVIEWS</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={onlyFlagged}
            onChange={(e) => setOnlyFlagged(e.target.checked)}
            data-testid="admin-moderation-only-flagged"
          />
          ONLY HIDDEN
        </label>
        <div className="text-xs text-text-secondary ml-auto">{rows.length} shown</div>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-moderation-list">
        {rows.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm">
            No {target} match.
          </div>
        )}
        {rows.map((r) => {
          const id = target === "posts" ? r.post_id : r.review_id;
          const author = r.user_name || r.reviewer_name || r.author || r.user_id || "unknown";
          const body = r.content || r.text || r.body || "";
          return (
            <div key={id} className="p-4 flex items-start gap-3" data-testid={`admin-moderation-row-${id}`}>
              <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${
                r.is_hidden ? "bg-red-500/15 text-red-600" : "bg-white/5 text-text-secondary"
              }`}>
                {r.is_hidden ? "HIDDEN" : "LIVE"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{author}</div>
                <div className="text-xs text-text-secondary mt-0.5 whitespace-pre-wrap break-words">{body || "(no text)"}</div>
                {r.created_at && (
                  <div className="text-[10px] text-text-secondary/60 mt-1">{new Date(r.created_at).toLocaleString()}</div>
                )}
              </div>
              <button
                onClick={() => toggleHide(id, r.is_hidden)}
                className={`px-3 py-1.5 text-[11px] label-tiny tracking-widest border transition-colors ${
                  r.is_hidden
                    ? "border-green-500 text-green-500 hover:!bg-green-500 hover:!text-surface-base"
                    : "border-white/20 text-text-secondary hover:!border-yellow-500 hover:!text-yellow-500"
                }`}
                data-testid={`admin-moderation-toggle-${id}`}
              >
                {r.is_hidden ? <><Eye size={11} weight="bold" className="inline mr-1" /> RESTORE</> : <><EyeSlash size={11} weight="bold" className="inline mr-1" /> HIDE</>}
              </button>
              {target === "posts" && (
                <button
                  onClick={() => purge(id)}
                  className="p-1.5 text-text-secondary hover:!text-red-500 transition-colors"
                  data-testid={`admin-moderation-delete-${id}`}
                  aria-label={`Delete ${id}`}
                >
                  <Trash size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


const RevenueTab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const load = useCallback(() => {
    api.get("/admin/revenue").then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load revenue"))
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (busy) return <div className="label-tiny text-text-secondary">Loading revenue...</div>;
  if (!data) return null;
  const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthCard title="MRR" value={inr(data.mrr_inr)} sub={`${data.active_pro_subs} active pro subs`} />
        <HealthCard title="ARR" value={inr(data.arr_inr)} sub={data.mom_growth_pct != null ? `${data.mom_growth_pct > 0 ? "+" : ""}${data.mom_growth_pct}% MoM` : "New month"} />
        <HealthCard title="LIFETIME REV" value={inr(data.lifetime_revenue_inr)} sub={`${data.paid_orders_lifetime} paid orders`} />
        <HealthCard title="LAST 30 DAYS" value={inr(data.revenue_30d_inr)} sub={`${data.orders_30d} orders`} />
        <HealthCard title="LTV" value={inr(data.ltv_inr)} sub="Avg lifetime value" />
        <HealthCard title="CHURN (30D)" value={data.churned_30d} sub="Subs expired last 30d" />
      </div>
      <div className="border border-white/10 p-5">
        <div className="label-tiny text-[10px] text-brand-accent mb-3">// CONVERSION FUNNEL</div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-heading tabular-nums">{data.funnel.signups.toLocaleString()}</div>
            <div className="text-xs text-text-secondary">SIGNUPS</div>
          </div>
          <div>
            <div className="text-3xl font-heading tabular-nums text-brand-heat">{data.funnel.activated.toLocaleString()}</div>
            <div className="text-xs text-text-secondary">ACTIVATED ({data.funnel.activation_pct}%)</div>
          </div>
          <div>
            <div className="text-3xl font-heading tabular-nums text-brand-accent">{data.funnel.paying.toLocaleString()}</div>
            <div className="text-xs text-text-secondary">PAID ({data.funnel.paid_pct}%)</div>
          </div>
        </div>
      </div>
      <div className="border border-white/10 divide-y divide-white/5" data-testid="admin-revenue-top-payers">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// TOP 10 PAYING USERS</div>
        {data.top_payers.map((p, i) => (
          <div key={p.user_id} className="p-3 flex items-center gap-3">
            <div className="w-6 text-text-secondary font-mono">{i + 1}</div>
            <div className="flex-1">
              <div className="text-sm">{p.name}</div>
              <div className="text-[11px] text-text-secondary">{p.email}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-heading text-brand-accent tabular-nums">{inr(p.amount_inr)}</div>
              <div className="text-[10px] text-text-secondary">{p.orders} orders</div>
            </div>
          </div>
        ))}
      </div>

      {data.academy && <AcademyRevenueSection academy={data.academy} inr={inr} onRenewed={load} />}
    </div>
  );
};

const PLAN_CHIP = {
  growth:     "bg-cyan-400/15 text-cyan-300",
  pro:        "bg-brand-accent/15 text-brand-accent",
  enterprise: "bg-amber-400/15 text-amber-300",
};

const AcademyRevenueSection = ({ academy, inr, onRenewed }) => {
  const bd = academy.breakdown || {};
  const planRows = ["growth", "pro", "enterprise"].map((p) => ({ plan: p, ...(bd[p] || { subs: 0, mrr_inr: 0 }) }));
  const [renewFor, setRenewFor] = useState(null);   // the expiring academy being renewed
  const [renewBilling, setRenewBilling] = useState("monthly");
  const [renewMode, setRenewMode] = useState("comp");
  const [renewBusy, setRenewBusy] = useState(false);

  const openRenew = (e) => {
    setRenewFor(e);
    setRenewBilling(e.billing === "yearly" ? "yearly" : "monthly");
    setRenewMode("comp");
  };
  const submitRenew = async () => {
    if (!renewFor) return;
    setRenewBusy(true);
    try {
      const { data } = await api.post(`/admin/academies/${renewFor.academy_id}/renew`, {
        billing: renewBilling, mode: renewMode,
      });
      const until = data.plan_until ? new Date(data.plan_until).toLocaleDateString() : "";
      toast.success(
        `${renewFor.academy_name} renewed (${renewMode === "comp" ? "complimentary" : "paid"}) — active until ${until}`,
        { duration: 5000 }
      );
      setRenewFor(null);
      onRenewed?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not renew academy");
    } finally {
      setRenewBusy(false);
    }
  };

  return (
    <div className="space-y-4 pt-2" data-testid="admin-academy-revenue">
      <div className="flex items-center gap-2">
        <Buildings size={16} weight="duotone" className="text-brand-heat" />
        <div className="label-tiny text-[10px] text-brand-heat">// ACADEMY SUBSCRIPTIONS</div>
        {academy.pricing_placeholder && (
          <span className="label-tiny text-[9px] bg-amber-400/15 text-amber-300 px-2 py-0.5" data-testid="academy-pricing-placeholder-chip">
            PLACEHOLDER PRICING
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthCard title="ACADEMY MRR" value={inr(academy.mrr_inr)} sub={`${academy.active_subs} active academies`} />
        <HealthCard title="ACADEMY ARR" value={inr(academy.arr_inr)} sub="Annualised run rate" />
        <HealthCard title="ACADEMY REV (30D)" value={inr(academy.revenue_30d_inr)} sub="Collected last 30 days" />
        <HealthCard title="ACADEMY LIFETIME" value={inr(academy.lifetime_revenue_inr)} sub={`${academy.paid_orders} paid upgrades`} />
      </div>

      {academy.expiring && academy.expiring.length > 0 && (
        <div className="border border-amber-400/40 bg-amber-400/[0.06]" data-testid="admin-academy-expiring">
          <div className="p-3 label-tiny text-[10px] text-amber-300 bg-amber-400/10 flex items-center gap-2">
            <Warning size={13} weight="fill" /> // EXPIRING SOON — NUDGE BEFORE THEY DROP TO STARTER
            <span className="ml-auto text-amber-200/80">{academy.expiring.length}</span>
          </div>
          <div className="divide-y divide-white/5">
            {academy.expiring.map((e, i) => {
              const urgent = e.days_left != null && e.days_left <= 7;
              return (
                <div key={e.academy_id || i} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`admin-academy-expiring-${i}`}>
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {e.academy_name}
                      <span className={`label-tiny text-[9px] px-2 py-0.5 ${PLAN_CHIP[e.plan] || "bg-white/10 text-white/70"}`}>{(e.plan || "—").toUpperCase()}</span>
                    </div>
                    <div className="text-[11px] text-text-secondary">{e.admin_email || "no admin contact"}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-heading tabular-nums ${urgent ? "text-red-400" : "text-amber-300"}`}>
                      {e.days_left != null ? `${e.days_left}d left` : "—"}
                    </div>
                    <div className="text-[10px] text-text-secondary">
                      {e.plan_until ? new Date(e.plan_until).toLocaleDateString() : "—"} · {e.comp ? "comp" : `${inr(e.mrr_inr)}/mo`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {e.admin_email && (
                      <a
                        href={`mailto:${e.admin_email}?subject=${encodeURIComponent(`Your Edge Training ${(e.plan || "").toUpperCase()} plan renews soon`)}&body=${encodeURIComponent(`Hi,\n\nYour academy "${e.academy_name}" is on the ${e.plan} plan and renews on ${e.plan_until ? new Date(e.plan_until).toLocaleDateString() : "soon"}. Renew to keep your unlocked seats and features — otherwise the academy reverts to the free Starter limits.\n\nThanks,\nEdge Training`)}`}
                        className="inline-flex items-center gap-1 border border-amber-400/40 text-amber-300 hover:!bg-amber-400 hover:!text-[#0A0A0F] px-2.5 py-1.5 text-[10px] label-tiny tracking-wider transition-colors"
                        data-testid={`admin-academy-nudge-${i}`}
                      >
                        <Envelope size={11} weight="bold" /> NUDGE
                      </a>
                    )}
                    <button
                      onClick={() => openRenew(e)}
                      className="inline-flex items-center gap-1 bg-brand-accent text-surface-base hover:!bg-brand-accent/90 px-2.5 py-1.5 text-[10px] label-tiny tracking-wider transition-colors"
                      data-testid={`admin-academy-renew-${i}`}
                    >
                      <Lightning size={11} weight="fill" /> RENEW
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {renewFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => !renewBusy && setRenewFor(null)}
          data-testid="admin-academy-renew-dialog"
        >
          <div
            className="w-full max-w-md border border-white/15 bg-[#0A0A0F] p-5 space-y-4"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-tiny text-[10px] text-brand-accent">// RENEW ON BEHALF</div>
                <div className="text-lg font-heading mt-1" data-testid="admin-academy-renew-name">{renewFor.academy_name}</div>
                <div className="text-[11px] text-text-secondary">
                  {(renewFor.plan || "").toUpperCase()} · currently until {renewFor.plan_until ? new Date(renewFor.plan_until).toLocaleDateString() : "—"}
                </div>
              </div>
              <button onClick={() => setRenewFor(null)} disabled={renewBusy} className="text-text-secondary hover:text-white disabled:opacity-40" data-testid="admin-academy-renew-close">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2">
              <div className="label-tiny text-[10px] text-text-secondary">DURATION</div>
              <div className="grid grid-cols-2 gap-2">
                {["monthly", "yearly"].map((b) => (
                  <button
                    key={b}
                    onClick={() => setRenewBilling(b)}
                    className={`py-2 text-[11px] label-tiny tracking-wider border transition-colors ${renewBilling === b ? "border-brand-accent bg-brand-accent/10 text-brand-accent" : "border-white/15 text-text-secondary hover:text-white"}`}
                    data-testid={`admin-academy-renew-billing-${b}`}
                  >
                    {b === "yearly" ? "+1 YEAR" : "+1 MONTH"}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="label-tiny text-[10px] text-text-secondary">RENEWAL TYPE</div>
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={() => setRenewMode("comp")}
                  className={`text-left px-3 py-2 border transition-colors ${renewMode === "comp" ? "border-brand-accent bg-brand-accent/10" : "border-white/15 hover:border-white/30"}`}
                  data-testid="admin-academy-renew-mode-comp"
                >
                  <div className="text-sm font-medium">Complimentary (free)</div>
                  <div className="text-[11px] text-text-secondary">Extends the plan at no charge · kept out of revenue &amp; MRR</div>
                </button>
                <button
                  onClick={() => setRenewMode("paid")}
                  className={`text-left px-3 py-2 border transition-colors ${renewMode === "paid" ? "border-brand-accent bg-brand-accent/10" : "border-white/15 hover:border-white/30"}`}
                  data-testid="admin-academy-renew-mode-paid"
                >
                  <div className="text-sm font-medium">Off-platform payment</div>
                  <div className="text-[11px] text-text-secondary">Records a paid renewal with an invoice · counts in revenue &amp; MRR</div>
                </button>
              </div>
            </div>

            <button
              onClick={submitRenew}
              disabled={renewBusy}
              className="w-full py-2.5 bg-brand-accent text-surface-base label-tiny text-[11px] tracking-wider hover:bg-brand-accent/90 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
              data-testid="admin-academy-renew-confirm"
            >
              <Lightning size={13} weight="fill" />
              {renewBusy ? "RENEWING…" : `CONFIRM ${renewMode === "comp" ? "COMP" : "PAID"} RENEWAL`}
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3" data-testid="admin-academy-plan-breakdown">
        {planRows.map((r) => (
          <div key={r.plan} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`academy-plan-tile-${r.plan}`}>
            <span className={`label-tiny text-[9px] px-2 py-0.5 ${PLAN_CHIP[r.plan]}`}>{r.plan.toUpperCase()}</span>
            <div className="text-2xl font-heading tabular-nums mt-2">{r.subs}</div>
            <div className="text-[11px] text-text-secondary">active · {inr(r.mrr_inr)}/mo MRR</div>
          </div>
        ))}
      </div>

      <div className="border border-white/10" data-testid="admin-academy-payments">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5 flex items-center justify-between">
          <span>// ALL ACADEMY PAYMENTS</span>
          <div className="flex items-center gap-3">
            {academy.payments.length > 0 && (
              <button
                onClick={() => downloadCsv("/admin/academy-payments/export.csv", "edge-academy-payments.csv")}
                className="inline-flex items-center gap-1.5 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-2.5 py-1 text-[10px] label-tiny tracking-widest transition-colors text-text-secondary"
                data-testid="admin-academy-payments-export-csv"
              >
                <DownloadSimple size={11} weight="bold" /> EXPORT CSV
              </button>
            )}
            <span className="text-text-secondary">{academy.payments.length}</span>
          </div>
        </div>
        {academy.payments.length === 0 ? (
          <div className="p-10 text-center text-text-secondary text-sm" data-testid="admin-academy-payments-empty">
            No academy upgrades yet. Paid academy plans will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-3 label-tiny text-[10px]">ACADEMY</th>
                  <th className="text-left p-3 label-tiny text-[10px]">PLAN</th>
                  <th className="text-left p-3 label-tiny text-[10px]">BILLING</th>
                  <th className="text-right p-3 label-tiny text-[10px]">AMOUNT</th>
                  <th className="text-left p-3 label-tiny text-[10px]">DATE</th>
                  <th className="text-left p-3 label-tiny text-[10px]">INVOICE</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {academy.payments.map((p, i) => (
                  <tr key={p.invoice_no || i} data-testid={`admin-academy-payment-${i}`}>
                    <td className="p-3">
                      <div className="font-medium">{p.academy_name}</div>
                      <div className="text-[10px] text-text-secondary">{p.payer_email || "—"}</div>
                    </td>
                    <td className="p-3">
                      <span className={`label-tiny text-[9px] px-2 py-0.5 ${PLAN_CHIP[p.plan] || "bg-white/10 text-white/70"}`}>{(p.plan || "—").toUpperCase()}</span>
                    </td>
                    <td className="p-3 text-text-secondary capitalize">{p.billing}</td>
                    <td className="p-3 text-right font-heading text-brand-accent tabular-nums">{inr(p.amount_inr)}</td>
                    <td className="p-3 text-text-secondary text-xs">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3 font-mono text-[11px] text-text-secondary">{p.invoice_no || "—"}</td>
                    <td className="p-3 text-right">
                      {p.invoice_no && (
                        <button
                          onClick={() => downloadFile(`/admin/academy-invoice/${p.invoice_no}`, `invoice-${p.invoice_no}.pdf`, "application/pdf")}
                          className="inline-flex items-center gap-1 border border-white/15 hover:!border-brand-heat hover:!text-brand-heat px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors"
                          data-testid={`admin-academy-invoice-download-${i}`}
                        >
                          <DownloadSimple size={11} weight="bold" /> PDF
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const CohortsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/admin/cohorts").then(({ data }) => setRows(data?.cohorts || []))
      .catch(() => toast.error("Could not load cohorts"))
      .finally(() => setBusy(false));
  }, []);
  if (busy) return <div className="label-tiny text-text-secondary">Loading cohorts...</div>;
  const heat = (pct) => {
    if (pct >= 30) return "bg-green-500/25 text-green-500";
    if (pct >= 15) return "bg-brand-heat/25 text-brand-heat";
    if (pct >= 5)  return "bg-yellow-500/20 text-yellow-500";
    return "bg-white/5 text-text-secondary";
  };
  return (
    <div className="space-y-4" data-testid="admin-cohorts">
      <div className="text-xs text-text-secondary">Monthly signup cohorts. Retention = users still active N days after joining.</div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-3 label-tiny text-[10px]">MONTH</th>
              <th className="text-right p-3 label-tiny text-[10px]">SIGNUPS</th>
              <th className="text-right p-3 label-tiny text-[10px]">1D</th>
              <th className="text-right p-3 label-tiny text-[10px]">7D</th>
              <th className="text-right p-3 label-tiny text-[10px]">30D</th>
              <th className="text-right p-3 label-tiny text-[10px]">CONVERTED</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-text-secondary">No cohort data yet.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.month} data-testid={`admin-cohort-${c.month}`}>
                <td className="p-3 font-mono text-xs">{c.month}</td>
                <td className="p-3 text-right tabular-nums">{c.signups}</td>
                <td className={`p-3 text-right tabular-nums text-xs font-semibold ${heat(c.retention_1d_pct)}`}>{c.retention_1d_pct}%</td>
                <td className={`p-3 text-right tabular-nums text-xs font-semibold ${heat(c.retention_7d_pct)}`}>{c.retention_7d_pct}%</td>
                <td className={`p-3 text-right tabular-nums text-xs font-semibold ${heat(c.retention_30d_pct)}`}>{c.retention_30d_pct}%</td>
                <td className="p-3 text-right tabular-nums text-brand-accent text-xs font-semibold">{c.converted_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnnouncementsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaLink, setCtaLink] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setRows(data?.items || []);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!msg.trim()) { toast.error("Message required"); return; }
    try {
      await api.post("/admin/announcements", { message: msg, cta_text: ctaText, cta_link: ctaLink, dismissible: true });
      toast.success("Announcement live");
      setMsg(""); setCtaText(""); setCtaLink("");
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const toggle = async (aid, current) => {
    try {
      await api.patch(`/admin/announcements/${aid}`, { is_active: !current });
      load();
    } catch {
      toast.error("Could not toggle");
    }
  };

  const remove = async (aid) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/admin/announcements/${aid}`);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-announcements">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-text-secondary">POST NEW ANNOUNCEMENT (top-of-app banner)</div>
        <Input placeholder="Message (280 chars max)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="admin-announcement-msg" />
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="CTA text (optional)" value={ctaText} onChange={(e) => setCtaText(e.target.value)} className="max-w-[200px]" data-testid="admin-announcement-cta-text" />
          <Input placeholder="CTA link (/upgrade)" value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} className="flex-1 min-w-[200px]" data-testid="admin-announcement-cta-link" />
          <button onClick={create} disabled={!msg.trim()} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="admin-announcement-post">
            POST
          </button>
        </div>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && (
          <div className="p-10 text-center text-text-secondary text-sm">No announcements yet.</div>
        )}
        {rows.map((a) => (
          <div key={a.announcement_id} className="p-4 flex items-start gap-3" data-testid={`admin-announcement-${a.announcement_id}`}>
            <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${a.is_active ? "bg-green-500/15 text-green-600" : "bg-white/5 text-text-secondary"}`}>
              {a.is_active ? "LIVE" : "OFF"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm">{a.message}</div>
              {a.cta_text && <div className="text-[11px] text-brand-accent mt-0.5">CTA: {a.cta_text} → {a.cta_link}</div>}
              <div className="text-[10px] text-text-secondary/70 mt-0.5">
                {a.updated_by} · {new Date(a.updated_at).toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => toggle(a.announcement_id, a.is_active)}
              className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-white/20 hover:!border-heat hover:!text-heat transition-colors"
              data-testid={`admin-announcement-toggle-${a.announcement_id}`}
            >
              {a.is_active ? "TURN OFF" : "TURN ON"}
            </button>
            <button onClick={() => remove(a.announcement_id)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`admin-announcement-delete-${a.announcement_id}`}>
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};



// ─── BATCH 3 TABS ─────────────────────────────────────────────────────────

const PACKAGE_OPTIONS = [
  { id: "pro_monthly", label: "Pro Monthly" },
  { id: "pro_yearly", label: "Pro Yearly" },
  { id: "coach_pro_monthly", label: "Coach Pro Monthly" },
  { id: "coach_pro_yearly", label: "Coach Pro Yearly" },
  { id: "coach_plus_monthly", label: "Coach Plus Monthly" },
];

const CouponsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [limit, setLimit] = useState("");
  const [expires, setExpires] = useState("");
  const [desc, setDesc] = useState("");
  const [scope, setScope] = useState("all");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setRows(data?.items || []);
    } catch { toast.error("Could not load coupons"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!code.trim() || !value) { toast.error("Code + value required"); return; }
    try {
      await api.post("/admin/coupons", {
        code: code.trim(),
        type,
        value: parseFloat(value),
        applies_to: scope === "all" ? ["all"] : [scope],
        usage_limit: limit ? parseInt(limit, 10) : null,
        expires_at: expires ? new Date(expires).toISOString() : "",
        description: desc,
      });
      toast.success(`Coupon ${code.toUpperCase()} live`);
      setCode(""); setValue(""); setLimit(""); setExpires(""); setDesc("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create coupon");
    }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.code}`, { is_active: !c.is_active });
      load();
    } catch { toast.error("Could not toggle"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${c.code}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-brand-accent mb-2">// CREATE COUPON</div>
        <div className="grid md:grid-cols-3 gap-2">
          <Input placeholder="CODE (e.g. WELCOME25)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="admin-coupon-code" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="admin-coupon-type">
            <option value="percent">Percent off (%)</option>
            <option value="flat">Flat off (₹)</option>
          </select>
          <Input placeholder={type === "percent" ? "e.g. 25" : "e.g. 100"} value={value} onChange={(e) => setValue(e.target.value)} data-testid="admin-coupon-value" type="number" />
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="admin-coupon-scope">
            <option value="all">All plans</option>
            {PACKAGE_OPTIONS.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
          </select>
          <Input placeholder="Usage limit (blank = unlimited)" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="admin-coupon-limit" type="number" />
          <Input placeholder="Expires (YYYY-MM-DD)" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="admin-coupon-expires" type="date" />
        </div>
        <div className="flex gap-2">
          <Input placeholder="Description (optional, internal)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="admin-coupon-desc" />
          <button onClick={create} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="admin-coupon-create">CREATE</button>
        </div>
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No coupons yet.</div>}
        {rows.map((c) => {
          const label = c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`;
          const usage = c.usage_limit ? `${c.used_count}/${c.usage_limit}` : `${c.used_count} used`;
          return (
            <div key={c.code} className="p-4 flex items-center gap-3" data-testid={`admin-coupon-row-${c.code}`}>
              <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${c.is_active ? "bg-green-500/15 text-green-600" : "bg-white/5 text-text-secondary"}`}>
                {c.is_active ? "LIVE" : "OFF"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono">{c.code}</div>
                <div className="text-[11px] text-text-secondary">
                  {label} · {usage}
                  {c.expires_at && ` · expires ${new Date(c.expires_at).toLocaleDateString()}`}
                  {c.applies_to && c.applies_to[0] !== "all" && ` · ${c.applies_to.join(", ")}`}
                </div>
                {c.description && <div className="text-[10px] text-text-secondary/70 mt-0.5">{c.description}</div>}
              </div>
              <button onClick={() => toggle(c)} className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-white/20 hover:!border-heat hover:!text-heat transition-colors" data-testid={`admin-coupon-toggle-${c.code}`}>
                {c.is_active ? "TURN OFF" : "TURN ON"}
              </button>
              <button onClick={() => remove(c)} className="p-1.5 text-text-secondary hover:!text-red-500" data-testid={`admin-coupon-delete-${c.code}`}>
                <Trash size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RefundsTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [refundBusy, setRefundBusy] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/payments?limit=100");
      setRows(data?.items || []);
    } catch { toast.error("Could not load payments"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refund = async (order) => {
    const reason = window.prompt(`Refund ₹${(order.amount_paise / 100).toFixed(2)} on order ${order.order_id}?\n\nReason (optional):`, "");
    if (reason === null) return;
    setRefundBusy(order.order_id);
    try {
      const { data } = await api.post(`/admin/payments/${order.order_id}/refund`, {
        reason,
        revoke_access: true,
      });
      toast.success(`Refunded ₹${data.amount_inr}${data.access_revoked ? " · access revoked" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setRefundBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-refunds">
      <div className="text-xs text-text-secondary">
        Recent Razorpay orders. Refunding a paid order calls Razorpay&apos;s refund API and (optionally) revokes access.
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No payments yet.</div>}
        {rows.map((o) => {
          const isPaid = o.status === "paid";
          const isRefunded = o.status === "refunded" || o.status === "partially_refunded";
          return (
            <div key={o.order_id} className="p-4 flex items-center gap-3" data-testid={`admin-refund-row-${o.order_id}`}>
              <div className={`label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 ${
                isPaid ? "bg-green-500/15 text-green-600"
                : isRefunded ? "bg-red-500/15 text-red-500"
                : "bg-white/5 text-text-secondary"
              }`}>
                {(o.status || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono">{o.order_id}</div>
                <div className="text-[11px] text-text-secondary">
                  ₹{(o.amount_paise / 100).toFixed(2)} · {o.package_id}
                  {o.coupon_code && ` · coupon ${o.coupon_code}`}
                </div>
                {o.refund_amount_paise && (
                  <div className="text-[10px] text-red-500/80 mt-0.5">
                    Refunded ₹{(o.refund_amount_paise / 100).toFixed(2)}
                    {o.refunded_by && ` by ${o.refunded_by}`}
                    {o.refund_reason && ` · ${o.refund_reason}`}
                  </div>
                )}
                <div className="text-[10px] text-text-secondary/70 mt-0.5">
                  user {o.user_id?.slice(0, 12)} · {o.created_at?.slice(0, 16)}
                </div>
              </div>
              {isPaid && !isRefunded && (
                <button
                  onClick={() => refund(o)}
                  disabled={refundBusy === o.order_id}
                  className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                  data-testid={`admin-refund-btn-${o.order_id}`}
                >
                  {refundBusy === o.order_id ? "..." : "REFUND"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ROLE_OPTIONS = [
  "athlete", "strength_coach", "fitness_trainer", "sports_coach",
  "academy_admin", "general_client", "parent_guardian",
];

const BroadcastTab = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [roles, setRoles] = useState([]);
  const [plan, setPlan] = useState("");
  const [only30d, setOnly30d] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const payload = () => ({
    title, body, link,
    roles: roles,
    plan: plan || null,
    only_active_30d: only30d,
    include_push: true,
  });

  const toggleRole = (r) => {
    setRoles((cur) => cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]);
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/preview", payload());
      setPreview(data);
      toast.info(`${data.count.toLocaleString()} user(s) match`);
    } catch { toast.error("Preview failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!window.confirm(`Send to ${preview?.count ?? "all matching"} users?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", payload());
      toast.success(`Sent to ${data.sent} of ${data.targeted}`);
      setTitle(""); setBody(""); setLink(""); setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-broadcast">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// BROADCAST TO SEGMENT</div>
        <Input placeholder="Title (160 chars)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-broadcast-title" />
        <Input placeholder="Body (optional, 400 chars)" value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-broadcast-body" />
        <Input placeholder="Link (optional, e.g. /upgrade)" value={link} onChange={(e) => setLink(e.target.value)} data-testid="admin-broadcast-link" />

        <div>
          <div className="label-tiny text-[10px] text-text-secondary mb-2">ROLES (empty = all)</div>
          <div className="flex flex-wrap gap-1">
            {ROLE_OPTIONS.map((r) => (
              <button
                key={r}
                onClick={() => toggleRole(r)}
                className={`px-3 py-1 text-[11px] label-tiny tracking-widest border ${
                  roles.includes(r)
                    ? "border-heat bg-heat/10 text-heat"
                    : "border-white/10 text-text-secondary hover:!border-heat"
                }`}
                data-testid={`admin-broadcast-role-${r}`}
              >
                {r.replace(/_/g, " ").toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-2">
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="admin-broadcast-plan">
            <option value="">Any plan</option>
            <option value="free">Free tier</option>
            <option value="pro">Active Pro</option>
            <option value="coach_pro">Active Coach Pro</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-text-secondary px-3 py-2 border border-white/10">
            <input type="checkbox" checked={only30d} onChange={(e) => setOnly30d(e.target.checked)} data-testid="admin-broadcast-active30" />
            ACTIVE IN LAST 30 DAYS
          </label>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={runPreview} disabled={busy} className="px-4 py-2 text-xs label-tiny tracking-widest border border-white/20 hover:!border-heat hover:!text-heat disabled:opacity-50" data-testid="admin-broadcast-preview">
            PREVIEW COUNT
          </button>
          <button onClick={send} disabled={busy || !title.trim()} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="admin-broadcast-send">
            SEND {preview ? `TO ${preview.count.toLocaleString()}` : ""}
          </button>
        </div>

        {preview && (
          <div className="text-[11px] text-text-secondary" data-testid="admin-broadcast-preview-result">
            → Will reach <span className="text-heat font-heading">{preview.count.toLocaleString()}</span> user(s).
            In-app notification + web push (where subscribed).
          </div>
        )}
      </div>
    </div>
  );
};

const SeederTab = () => {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(10);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/seed/runs");
      setRuns(data?.items || []);
    } catch { toast.error("Could not load seed runs"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/seed/demo?count=${count}`);
      toast.success(`Seeded ${data.users} users, ${data.workouts} workouts, ${data.prs} PRs`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally { setBusy(false); }
  };

  const purge = async (r) => {
    if (!window.confirm(`Purge ${r.user_count} users + ${r.workout_count} workouts + ${r.pr_count} PRs?`)) return;
    try {
      await api.delete(`/admin/seed/${r.seed_run_id}`);
      toast.success("Purged");
      load();
    } catch { toast.error("Purge failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-seeder">
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-brand-accent mb-2">// SEED DEMO DATA</div>
        <p className="text-xs text-text-secondary">
          Creates N demo athletes with workouts + PRs, tagged with a seed_run_id.
          Environment-safe — real users are never touched.
        </p>
        <div className="flex gap-2 items-center">
          <label className="label-tiny text-[10px] text-text-secondary">COUNT</label>
          <Input type="number" min="1" max="200" value={count} onChange={(e) => setCount(parseInt(e.target.value, 10) || 10)} className="max-w-[100px]" data-testid="admin-seeder-count" />
          <button onClick={seed} disabled={busy} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="admin-seeder-run">
            {busy ? "SEEDING..." : "SEED"}
          </button>
        </div>
      </div>
      <div className="border border-white/10 divide-y divide-white/5">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// SEED RUNS ({runs.length})</div>
        {runs.length === 0 && <div className="p-10 text-center text-text-secondary text-sm">No seed runs yet.</div>}
        {runs.map((r) => (
          <div key={r.seed_run_id} className="p-4 flex items-center gap-3" data-testid={`admin-seed-run-${r.seed_run_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-mono">{r.seed_run_id.slice(0, 8)}...</div>
              <div className="text-[11px] text-text-secondary">
                {r.user_count} users · {r.workout_count} workouts · {r.pr_count} PRs
              </div>
              <div className="text-[10px] text-text-secondary/70 mt-0.5">
                {r.created_by} · {new Date(r.created_at).toLocaleString()}
              </div>
            </div>
            <button onClick={() => purge(r)} className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-red-500/40 text-red-500 hover:bg-red-500/10" data-testid={`admin-seed-purge-${r.seed_run_id}`}>
              PURGE
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ImpersonationLogTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/admin/impersonation-log")
      .then(({ data }) => setRows(data?.items || []))
      .catch(() => toast.error("Could not load"))
      .finally(() => setBusy(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-impersonation-log">
      <div className="text-xs text-text-secondary">
        Every time an admin impersonates a user is recorded here (via the audit log).
      </div>
      {busy && <div className="label-tiny text-text-secondary">Loading...</div>}
      <div className="border border-white/10 divide-y divide-white/5">
        {rows.length === 0 && !busy && <div className="p-10 text-center text-text-secondary text-sm">No impersonation events yet.</div>}
        {rows.map((r) => (
          <div key={r.log_id} className="p-4 flex items-center gap-3" data-testid={`admin-impersonation-row-${r.log_id}`}>
            <div className="label-tiny text-[10px] tracking-widest px-2 py-1 shrink-0 bg-brand-heat/15 text-brand-heat">
              IMPERSONATE
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm">
                <span className="text-brand-accent">{r.admin_email}</span>
                <span className="text-text-secondary"> → </span>
                <span>{r.target_email || r.target_id}</span>
                {r.target_role && <span className="text-[10px] text-text-secondary ml-2">({r.target_role})</span>}
              </div>
              <div className="text-[11px] text-text-secondary">
                {new Date(r.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── BATCH 4 — CLOSING PUSH ────────────────────────────────────────────────

const OpsInbox = () => {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/admin/ops-inbox").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <div className="border border-heat/30 bg-heat/[0.03] p-5 space-y-4" data-testid="admin-ops-inbox">
      <div className="flex items-center justify-between">
        <div className="label-tiny text-heat text-[11px] tracking-widest">// ATTENTION QUEUE</div>
        <div className="text-[11px] text-text-secondary tabular-nums">
          {data.activity.signups_24h} signup(s) · ₹{data.activity.revenue_7d_inr.toLocaleString()} rev · 7d
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {data.attention.map((a) => (
          <button
            key={a.kind}
            onClick={() => nav(a.link)}
            className={`text-left border p-3 transition ${
              a.count > 0
                ? "border-heat/40 hover:!border-heat hover:bg-heat/5"
                : "border-white/10 opacity-60"
            }`}
            data-testid={`ops-inbox-${a.kind}`}
          >
            <div className={`text-2xl font-heading tabular-nums ${a.count > 0 ? "text-heat" : "text-text-secondary"}`}>{a.count}</div>
            <div className="text-[10px] label-tiny text-text-secondary mt-1">{a.label.toUpperCase()}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
);

const GlobalSearchBar = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const setParams = useSearchParams()[1];
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data.items || []);
        setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.querySelector('[data-testid="admin-global-search-input"]')?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const kindStyle = {
    user: { color: "text-brand-accent", label: "USER" },
    academy: { color: "text-heat", label: "ACADEMY" },
    payment: { color: "text-lime-500", label: "PAYMENT" },
    audit: { color: "text-cyan-400", label: "AUDIT" },
  };
  return (
    <div className="relative" data-testid="admin-global-search">
      <div className="flex items-center gap-2 border border-white/10 bg-white/[0.02] px-3 py-2 focus-within:!border-brand-accent">
        <MagnifyingGlass size={14} className="text-text-secondary" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search users, academies, payments, audit... (⌘K)"
          className="bg-transparent outline-none flex-1 text-sm placeholder:text-text-secondary/60"
          data-testid="admin-global-search-input"
        />
        {q && <button onClick={() => setQ("")} className="text-text-secondary hover:text-heat" data-testid="admin-global-search-clear"><XIcon size={12} /></button>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 border border-white/15 bg-surface-DEFAULT max-h-96 overflow-auto divide-y divide-white/5" data-testid="admin-global-search-results">
          {results.map((r) => (
            <button
              key={`${r.kind}-${r.id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (r.kind === "user") {
                  setParams({ tab: "user-detail", user_id: r.id });
                } else {
                  nav(r.link);
                }
                setOpen(false);
                setQ("");
              }}
              className="p-3 text-left w-full flex items-center gap-3 hover:bg-white/[0.03]"
              data-testid={`admin-search-hit-${r.kind}-${r.id}`}
            >
              <span className={`label-tiny text-[9px] tracking-widest px-1.5 py-0.5 border shrink-0 ${kindStyle[r.kind]?.color}`}>
                {kindStyle[r.kind]?.label}
              </span>
              <span className="flex-1 min-w-0">
                <div className="text-sm truncate">{r.title}</div>
                <div className="text-[11px] text-text-secondary truncate">{r.subtitle}</div>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const REPORT_KINDS = [
  { id: "users", label: "Users" },
  { id: "payments", label: "Payments" },
  { id: "audit", label: "Audit Logs" },
  { id: "seed_runs", label: "Seed Runs" },
];

const ReportsTab = () => {
  const [kind, setKind] = useState("users");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [role, setRole] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const buildBody = () => ({
    kind,
    date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
    date_to: dateTo ? new Date(dateTo).toISOString() : null,
    role: kind === "users" && role ? role : null,
    plan: kind === "users" && plan ? plan : null,
    status: kind !== "users" && status ? status : null,
    limit: 5000,
  });

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/run", buildBody());
      setPreview(data);
      toast.info(`${data.count} row(s) match`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const downloadReport = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/reports/csv", buildBody(), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${kind}-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-reports">
      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// BUILD REPORT</div>
        <div className="grid md:grid-cols-3 gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="report-kind">
            {REPORT_KINDS.map((k) => (<option key={k.id} value={k.id}>{k.label}</option>))}
          </select>
          <Input type="date" placeholder="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-from" />
          <Input type="date" placeholder="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-to" />
        </div>
        {kind === "users" && (
          <div className="grid md:grid-cols-2 gap-2">
            <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="report-role">
              <option value="">Any role</option>
              {["athlete","strength_coach","fitness_trainer","sports_coach","academy_admin","general_client","parent","super_admin"].map((r) => (<option key={r} value={r}>{r.replace(/_/g," ")}</option>))}
            </select>
            <select value={plan} onChange={(e) => setPlan(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="report-plan">
              <option value="">Any plan</option>
              <option value="free">Free</option>
              <option value="pro">Active Pro</option>
              <option value="coach_pro">Active Coach Pro</option>
            </select>
          </div>
        )}
        {kind === "payments" && (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="report-status">
            <option value="">Any status</option>
            <option value="paid">Paid</option>
            <option value="created">Created</option>
            <option value="refunded">Refunded</option>
            <option value="partially_refunded">Partially Refunded</option>
          </select>
        )}
        <div className="flex gap-2">
          <button onClick={runPreview} disabled={busy} className="px-4 py-2 text-xs label-tiny tracking-widest border border-white/20 hover:!border-heat hover:!text-heat disabled:opacity-50" data-testid="report-preview">
            PREVIEW
          </button>
          <button onClick={downloadReport} disabled={busy} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="report-download">
            EXPORT CSV
          </button>
          {preview && <div className="text-[11px] text-text-secondary self-center" data-testid="report-count">{preview.count} row(s)</div>}
        </div>
      </div>
      {preview && preview.items.length > 0 && (
        <div className="border border-white/10 overflow-auto max-h-[500px]" data-testid="report-preview-table">
          <table className="w-full text-[11px]">
            <thead className="bg-white/5">
              <tr>
                {preview.columns.map((c) => (
                  <th key={c} className="text-left px-2 py-2 label-tiny text-[9px] text-text-secondary tracking-widest">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {preview.items.slice(0, 100).map((r, i) => (
                <tr key={i}>
                  {preview.columns.map((c) => (
                    <td key={c} className="px-2 py-1.5 font-mono truncate max-w-[200px]">{String(r[c] ?? "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DetailSection = ({ title, items, empty, testId, render }) => (
  <div className="border border-white/10" data-testid={testId}>
    <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// {title} ({items.length})</div>
    {items.length === 0 && <div className="p-6 text-center text-text-secondary text-sm">{empty}</div>}
    {items.map((item, i) => (
      <div key={item.event_id || item.log_id || item.order_id || item.fee_id || item.academy_id || i} className="p-3 border-t border-white/5 first:border-t-0">
        {render(item)}
      </div>
    ))}
  </div>
);

const UserDetailTab = ({ userId, onBack }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);
  const [newNote, setNewNote] = useState("");

  const load = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setD(data);
    } catch { toast.error("Could not load user"); }
    finally { setBusy(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/users/${userId}/notes`, { note: newNote });
      setNewNote("");
      toast.success("Note added");
      load();
    } catch { toast.error("Could not add note"); }
  };

  if (!userId) return (
    <div className="p-6 text-center text-text-secondary text-sm" data-testid="admin-user-detail-empty">
      Pass ?user_id=<code>X</code> to view a user, or use the global search.
      <button onClick={onBack} className="ml-3 label-tiny text-heat">← Back</button>
    </div>
  );
  if (busy || !d) return <div className="label-tiny text-text-secondary">Loading user...</div>;

  const u = d.user;
  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="label-tiny text-[10px] text-text-secondary hover:!text-heat" data-testid="user-detail-back">← BACK</button>
        <div className="label-tiny text-[10px] text-brand-accent">// USER DETAIL</div>
      </div>
      <div className="border border-white/10 p-4 flex items-start gap-4">
        <div className="w-14 h-14 bg-white/5 flex items-center justify-center font-heading text-xl">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-xl">{u.name || u.email}</div>
          <div className="text-xs text-text-secondary">{u.email}</div>
          <div className="text-[10px] text-text-secondary mt-1 flex flex-wrap gap-2">
            <span>ROLE: {u.primary_role || u.role || "user"}</span>
            {u.pro_until && <span>· PRO until {u.pro_until.slice(0, 10)}</span>}
            {u.coach_pro_until && <span>· COACH PRO until {u.coach_pro_until.slice(0, 10)}</span>}
            {u.is_verified && <span className="text-brand-accent">· VERIFIED</span>}
            {u.is_suspended && <span className="text-red-500">· SUSPENDED</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading text-heat tabular-nums">{d.notif_unread}</div>
          <div className="text-[10px] text-text-secondary">UNREAD NOTIFS</div>
        </div>
      </div>

      <DetailSection title="PAYMENTS" empty="No payments." items={d.payments} testId="user-detail-payments" render={(p) => (
        <>
          <div className="text-sm font-mono">{p.order_id}</div>
          <div className="text-[11px] text-text-secondary">₹{(p.amount_paise/100).toFixed(2)} · {p.package_id} · {p.status}{p.coupon_code && ` · ${p.coupon_code}`}</div>
        </>
      )} />

      <DetailSection title="ACADEMY MEMBERSHIPS" empty="No memberships." items={d.memberships} testId="user-detail-memberships" render={(m) => (
        <>
          <div className="text-sm">{m.academy_name}</div>
          <div className="text-[11px] text-text-secondary">{m.role} @ /academy/{m.academy_slug}</div>
        </>
      )} />

      <DetailSection title="FEES" empty="No fees." items={d.fees} testId="user-detail-fees" render={(f) => (
        <>
          <div className="text-sm">₹{f.amount_inr.toFixed(2)} · {f.cycle} · due {f.due_date}</div>
          <div className={`text-[11px] ${f.status === "paid" ? "text-green-500" : f.status === "overdue" ? "text-red-500" : "text-text-secondary"}`}>{f.status.toUpperCase()}</div>
        </>
      )} />

      <DetailSection title="LOGIN HISTORY" empty="No login events." items={d.logins} testId="user-detail-logins" render={(l) => (
        <>
          <div className="text-sm font-mono">{l.ip} · {l.device}</div>
          <div className="text-[11px] text-text-secondary">{l.provider} · {new Date(l.created_at).toLocaleString()}</div>
        </>
      )} />

      <DetailSection title="AUDIT · ACTIONS ON THIS USER" empty="No audit rows." items={d.audit} testId="user-detail-audit" render={(a) => (
        <>
          <div className="text-sm">{a.action}</div>
          <div className="text-[11px] text-text-secondary">by {a.admin_email} · {new Date(a.created_at).toLocaleString()}</div>
        </>
      )} />

      <div className="border border-white/10">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// ADMIN NOTES ({d.notes.length})</div>
        <div className="p-3 flex gap-2 border-b border-white/5">
          <Input placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="user-detail-note-input" />
          <button onClick={addNote} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="user-detail-note-add">ADD</button>
        </div>
        {d.notes.length === 0 && <div className="p-6 text-center text-text-secondary text-sm">No notes yet.</div>}
        {d.notes.map((n) => (
          <div key={n.note_id} className="p-3 border-t border-white/5" data-testid={`user-detail-note-${n.note_id}`}>
            <div className="text-sm">{n.note}</div>
            <div className="text-[10px] text-text-secondary mt-1">
              <span className="text-brand-accent">{n.kind}</span> · {n.admin_email} · {new Date(n.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KILL_MODES = [
  { id: "off",    label: "OFF",    hint: "Normal operation" },
  { id: "banner", label: "BANNER", hint: "Show maintenance banner but keep everything writable" },
  { id: "hard",   label: "HARD",   hint: "Block all non-admin POST/PATCH/DELETE with 503" },
];

const KillswitchTab = () => {
  const [state, setState] = useState(null);
  const [mode, setMode] = useState("off");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/killswitch");
      setState(data);
      setMode(data.mode || "off");
      setMsg(data.message || "");
    } catch { toast.error("Could not load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (mode === "hard" && !window.confirm("HARD mode will block all non-admin writes with 503. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/admin/killswitch", { mode, message: msg });
      toast.success(`Maintenance mode: ${mode.toUpperCase()}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-killswitch">
      <div className={`border p-4 ${state?.mode === "hard" ? "border-red-500/40 bg-red-500/5" : state?.mode === "banner" ? "border-yellow-500/40 bg-yellow-500/5" : "border-white/10"}`}>
        <div className="label-tiny text-[10px] text-text-secondary mb-2">// CURRENT MODE</div>
        <div className={`text-2xl font-heading tracking-widest ${state?.mode === "hard" ? "text-red-500" : state?.mode === "banner" ? "text-yellow-500" : "text-brand-accent"}`}>
          {(state?.mode || "off").toUpperCase()}
        </div>
        {state?.updated_at && (
          <div className="text-[11px] text-text-secondary mt-1">
            Last change: {new Date(state.updated_at).toLocaleString()} by {state.updated_by}
          </div>
        )}
      </div>

      <div className="border border-white/10 p-4 space-y-3">
        <div className="label-tiny text-[10px] text-brand-accent">// SET MAINTENANCE MODE</div>
        <div className="grid md:grid-cols-3 gap-2">
          {KILL_MODES.map((k) => (
            <button
              key={k.id}
              onClick={() => setMode(k.id)}
              className={`text-left border p-3 transition ${
                mode === k.id ? "border-heat bg-heat/10" : "border-white/10 hover:!border-white/30"
              }`}
              data-testid={`kill-mode-${k.id}`}
            >
              <div className={`text-lg font-heading ${mode === k.id ? "text-heat" : "text-brand-accent"}`}>{k.label}</div>
              <div className="text-[11px] text-text-secondary mt-1">{k.hint}</div>
            </button>
          ))}
        </div>
        <Input placeholder="Message to display (optional, 280 chars)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="kill-msg" />
        <button onClick={save} disabled={busy} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="kill-save">
          APPLY MODE
        </button>
      </div>
    </div>
  );
};

const RolesTab = () => {
  const [admins, setAdmins] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/system-admins");
      setAdmins(data.items || []);
    } catch { toast.error("Could not load"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setHits((data.items || []).filter((h) => h.kind === "user"));
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const grant = async (userId, email) => {
    const reason = window.prompt(`Grant SUPER_ADMIN to ${email}?\nReason (audit log):`, "");
    if (reason === null) return;
    try {
      await api.patch(`/admin/users/${userId}/system-role`, { role: "super_admin", reason });
      toast.success(`Granted super_admin to ${email}`);
      setQ(""); setHits([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revoke = async (r) => {
    if (r.is_env_seeded) { toast.error("Env-seeded admin cannot be revoked at runtime"); return; }
    const reason = window.prompt(`Revoke SUPER_ADMIN from ${r.email}?\nReason (audit log):`, "");
    if (reason === null) return;
    try {
      await api.patch(`/admin/users/${r.user_id}/system-role`, { role: "user", reason });
      toast.success(`Revoked ${r.email}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-roles">
      <div className="border border-white/10">
        <div className="p-3 label-tiny text-[10px] text-brand-accent bg-white/5">// CURRENT SUPER ADMINS ({admins.length})</div>
        {busy && <div className="p-6 label-tiny text-text-secondary">Loading...</div>}
        {admins.map((r) => (
          <div key={r.user_id} className="p-4 flex items-center gap-3 border-t border-white/5" data-testid={`admin-role-row-${r.user_id}`}>
            <ShieldStar size={16} weight="fill" className="text-heat shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm">{r.name || r.email}</div>
              <div className="text-[11px] text-text-secondary">
                {r.email}
                {r.is_env_seeded && <span className="ml-2 text-brand-accent">· ENV-SEEDED</span>}
              </div>
            </div>
            {!r.is_env_seeded && (
              <button onClick={() => revoke(r)} className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-red-500/40 text-red-500 hover:bg-red-500/10" data-testid={`admin-role-revoke-${r.user_id}`}>
                REVOKE
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="border border-white/10 p-4 space-y-2">
        <div className="label-tiny text-[10px] text-brand-accent">// GRANT SUPER_ADMIN TO USER</div>
        <Input placeholder="Search by name / email..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-role-search" />
        {hits.length > 0 && (
          <div className="border border-white/10 divide-y divide-white/5">
            {hits.map((h) => (
              <div key={h.id} className="p-3 flex items-center gap-3" data-testid={`admin-role-hit-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{h.title}</div>
                  <div className="text-[11px] text-text-secondary truncate">{h.subtitle}</div>
                </div>
                <button onClick={() => grant(h.id, h.title)} className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-heat/40 text-heat hover:bg-heat/10" data-testid={`admin-role-grant-${h.id}`}>
                  GRANT
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

